import { confirmDialog } from '@theme/confirm-dialog';

(() => {
  'use strict';

  const configs = window.__pcwConfig || {};
  const sectionId = Object.keys(configs)[0];
  if (!sectionId) return;

  const cfg = configs[sectionId];
  // Assistent-Texte (Section-Settings) — früh deklariert, da buildDeviceCatalog sie nutzt.
  const ASSIST = cfg.assist || {};

  // Gehäuse-Auswahl. Ausgeschaltet (Standard) gilt für jede Immobilie das Standard-Gehäuse
  // und der Assistent sieht aus wie bisher.
  const HOUSING_ENABLED = (cfg.gehaeuse || {}).enabled === true;
  const HOUSING_DEFAULT = (cfg.gehaeuse || {}).default || 'Wohnbereich';
  const housingKey = (/** @type {any} */ v) => String(v == null ? '' : v).trim().toLowerCase();

  // Texte der Warenkorb-Rückfragen. Defaults hier, weil die Vorlagen geschweifte Klammern
  // enthalten und deshalb keinen Liquid-Default bekommen können.
  const LBL = Object.assign(
    {
      title: 'Anderes Gehäuse verwenden?',
      intro: 'Das gewünschte Gehäuse ist nicht in der benötigten Menge lieferbar.',
      linePartial: '{label}: {available}× {wanted} verfügbar, {fallback} ergänzen',
      lineNone: '{label}: {wanted} nicht verfügbar, {fallback} verwenden',
      note: 'Technisch identisch – nur das Gehäuse unterscheidet sich. Der Preis bleibt gleich.',
      confirm: 'Ja, so übernehmen',
      cancel: 'Abbrechen',
      checkTitle: 'Bitte kurz bestätigen',
      checkNote: 'So in den Warenkorb legen?',
    },
    // Leere Section-Settings dürfen die Defaults nicht überschreiben.
    Object.fromEntries(Object.entries(cfg.housing || {}).filter(([, v]) => v !== '' && v != null))
  );

  /**
   * Ersetzt {platzhalter} in einer Textvorlage. Fehlende Werte werden zu leerem Text,
   * damit eine unvollständig gepflegte Vorlage keine geschweiften Klammern zeigt.
   *
   * Fehlt die Vorlage selbst, werden die Werte roh aneinandergehängt statt String(undefined)
   * auszugeben. Diese Texte tragen Kaufhinweise — eine kaputte Vorlage darf den Hinweis
   * unleserlich machen, aber nicht verschwinden lassen.
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  function fmtLabel(template, values) {
    if (typeof template !== 'string' || template === '') {
      console.warn('[PCW] Textvorlage fehlt — Werte werden roh ausgegeben.', values);
      return Object.values(values).join(' · ');
    }
    return template.replace(/\{(\w+)\}/g, (_, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : ''
    );
  }
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  const root = document.getElementById(sectionId);
  if (!root) return;

  // Wohneinheiten-Obergrenze: 0 = deaktiviert. Über diesem Wert wird keine
  // Berechnung durchgeführt, sondern auf die Kontaktseite verwiesen.
  const MAX_WE = (cfg.weLimitEnabled && Number(cfg.maxWE) > 0) ? Number(cfg.maxWE) : 0;

  // Sperrt die jeweilige OMS-Antwort den „Weiter"-Weg? Fehlender Wert = sperren (altes Verhalten).
  const OMS_BLOCK = {
    nein: cfg.omsBlock?.nein !== false,
    unsicher: cfg.omsBlock?.unsicher !== false,
  };

  // Slider-Maxima (Theme-Editor konfigurierbar).
  const MAX_OBJEKTE = Number(cfg.maxObjekte) > 0 ? Number(cfg.maxObjekte) : 50;
  // Start-Anzahl Immobilien (Theme-Editor konfigurierbar). Auf [1, MAX_OBJEKTE] begrenzt.
  const START_OBJEKTE = Math.min(MAX_OBJEKTE, Math.max(1, Number(cfg.startObjekte) > 0 ? Number(cfg.startObjekte) : 1));
  const MAX_WE_PRO_OBJEKT = Number(cfg.maxWeProObjekt) > 0 ? Number(cfg.maxWeProObjekt) : 120;
  const WE_STEP = Number(cfg.weStep) > 0 ? Number(cfg.weStep) : 1;
  const WE_SCALE_MIN = cfg.weScaleMin != null ? String(cfg.weScaleMin) : '1';
  // Wie viele Wohneinheiten deckt EIN Datensammler ab (Theme-Editor konfigurierbar).
  const WE_PRO_DS = Number(cfg.wePerDatensammler) > 0 ? Number(cfg.wePerDatensammler) : 20;

  // Rundet eine WE-Zahl auf ein gültiges Slider-Vielfaches (min 1, Schrittweite WE_STEP,
  // max MAX_WE_PRO_OBJEKT). Ohne das „springt" der Range-Thumb beim ersten Ziehen, sobald ein
  // Admin eine Schrittweite > 1 setzt und der Startwert nicht auf dem Raster liegt — Thumb,
  // Wertlabel und state.we würden bis zur ersten Interaktion auseinanderlaufen.
  function snapWe(we) {
    const min = 1;
    const max = MAX_WE_PRO_OBJEKT;
    let v = Number(we);
    if (!isFinite(v)) v = min;
    v = Math.max(min, Math.min(max, v));
    const snapped = min + Math.round((v - min) / WE_STEP) * WE_STEP;
    return Math.max(min, Math.min(max, snapped));
  }
  // Standard-WE je neuer Immobilie (Theme-Editor konfigurierbar) — aufs Raster gerundet,
  // damit Thumb/Label/State ab dem ersten Frame übereinstimmen (statt eines fixen Werts,
  // der bei Schrittweite > 1 daneben liegt).
  const START_WE = Number(cfg.startWe) > 0 ? Number(cfg.startWe) : 4;
  const DEFAULT_WE = snapWe(START_WE);

  // Preselected device type for a fresh property (section setting). The answer pairs
  // mirror recType(): no socket = battery/SIM, with socket it depends on WLAN.
  // 'none' leaves both questions open, which is the behaviour before this setting.
  /** @type {Record<string, {s: boolean|null, w: boolean|null}>} */
  const DEVICE_ANSWERS = {
    batt: { s: false, w: null },
    ns: { s: true, w: false },
    nw: { s: true, w: true },
    none: { s: null, w: null },
  };
  const DEFAULT_DEVICE = DEVICE_ANSWERS[String(cfg.assistDefaultDevice || 'batt')]
    ? String(cfg.assistDefaultDevice || 'batt')
    : 'batt';

  /**
   * A property record. A newly added property copies the answers of the one before it,
   * because a portfolio is usually built from similar buildings — and because the bulk
   * card can only show ONE answer for the whole set. Falling back to the global default
   * here would silently mix an already-answered set with a differently answered newcomer.
   * Only the very first property (no predecessor) uses the configured preselection.
   * Units stay at the default: they have their own slider and get redistributed in bulk.
   * `seen` starts false: inherited answers are a suggestion, not a confirmation (see isComplete).
   * @param {{s: boolean|null, w: boolean|null, g: string|null} | undefined} [previous]
   */
  function makeProperty(previous) {
    const answers = previous || DEVICE_ANSWERS[DEFAULT_DEVICE];
    return {
      s: answers.s,
      w: answers.w,
      g: previous ? previous.g : null,
      we: DEFAULT_WE,
      seen: false,
    };
  }

  // Bulk mode: above this number of properties the assistant asks for ONE total number
  // of units instead of one per property. Asking twelve times in a row is busywork, and
  // at that size the customer rarely knows the per-building split anyway.
  const BULK_ENABLED = (cfg.bulk || {}).enabled !== false;
  const BULK_THRESHOLD = Math.max(1, Number((cfg.bulk || {}).threshold) || 5);
  // Copy of the bulk card. {n} is replaced with the property count (see fmtCount).
  const BULK_TITLE = (cfg.bulk || {}).title || 'Alle {n} Immobilien';
  const BULK_NOTE = (cfg.bulk || {}).note || '';
  const BULK_LABEL_WE = (cfg.bulk || {}).labelWe || 'Wohneinheiten gesamt';
  // Shown when the properties carry different answers and the single card therefore has
  // nothing to display. Deliberately not an "n still open" count — none of them is open.
  const BULK_MIXED_ALERT = (cfg.bulk || {}).mixedAlert
    || 'Ihre Immobilien haben unterschiedliche Angaben. Bitte wählen Sie oben eine Angabe, die für alle gilt.';

  // Kontakt-Gate (Section-Setting): aktiv → Schritt 2 verlangt Name/E-Mail + Captcha,
  // bevor das Ergebnis (Schritt 3) erscheint (Lead-Erfassung per Contact-Form). Aus →
  // „Weiter" berechnet direkt. Steuert Sichtbarkeit des Weiter-Buttons in Schritt 2,
  // die Step-3-Sperre und den Captcha-Submit-Flow.
  const CONTACT_GATE = cfg.contactGateEnabled === true;

  const INQUIRE_LABEL = (cfg.labels && cfg.labels.inquirePrice) || 'Auf Anfrage';

  // Offer PDF (section settings). Without the block there is no download.
  const PDF = cfg.pdf || {};

  const prefersReducedMotion = (() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (_) { return false; }
  })();

  // Inline-SVGs für die Gerätetypen (kein externer Icon-Font — Theme-Vorgabe).
  const SVG_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"';
  const ICON_WIFI = `<svg width="20" height="20" ${SVG_ATTRS}><path d="M5 12.55a11 11 0 0 1 14 0"></path><path d="M8.5 16.1a6 6 0 0 1 7 0"></path><path d="M2 8.82a15 15 0 0 1 20 0"></path><path d="M12 20h.01"></path></svg>`;
  const ICON_SIM = `<svg width="20" height="20" ${SVG_ATTRS}><path d="M16 2H8L4 6v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"></path><rect x="8" y="12" width="8" height="6" rx="1"></rect></svg>`;
  const ICON_BATT = `<svg width="20" height="20" ${SVG_ATTRS}><rect x="2" y="7" width="16" height="10" rx="2"></rect><path d="M22 11v2"></path><path d="M9 10v4"></path></svg>`;
  // Fragezeichen-Icon für „noch offen".
  const ICON_HELP = `<svg width="20" height="20" ${SVG_ATTRS}><circle cx="12" cy="12" r="9"></circle><path d="M9.5 9.5a2.5 2.5 0 0 1 4 2c0 1.5-2 2-2 3"></path><path d="M12 17h.01"></path></svg>`;
  // Häkchen für abgeschlossene Immobilien.
  const ICON_CHECK = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 13l4 4L19 7"></path></svg>`;

  // --- Debug-Overlay (für iPhone-Tests via ?pcw_debug=1) ---
  const DEBUG_ENABLED = (() => {
    try {
      return new URLSearchParams(window.location.search).get('pcw_debug') === '1';
    } catch (_) { return false; }
  })();

  let debugOverlay = null;
  const debugLines = [];
  function pcwDebug(msg) {
    const line = `[${new Date().toLocaleTimeString('de-DE', { hour12: false })}] ${msg}`;
    console.log('[PCW]', msg);
    if (!DEBUG_ENABLED) return;
    debugLines.push(line);
    if (debugLines.length > 12) debugLines.shift();
    if (!debugOverlay) {
      debugOverlay = document.createElement('div');
      debugOverlay.id = 'pcw-debug-overlay';
      debugOverlay.style.cssText = 'position:fixed;bottom:0;left:0;right:0;background:rgba(0,0,0,0.88);color:#9eff9e;font:11px/1.4 monospace;padding:8px 12px;z-index:2147483645;max-height:35vh;overflow-y:auto;pointer-events:auto;border-top:2px solid #006874;';
      const closeBtn = document.createElement('button');
      closeBtn.textContent = '×';
      closeBtn.style.cssText = 'position:absolute;top:4px;right:8px;background:none;border:0;color:#fff;font-size:18px;cursor:pointer;';
      closeBtn.addEventListener('click', () => { debugOverlay.style.display = 'none'; });
      debugOverlay.appendChild(closeBtn);
      const pre = document.createElement('pre');
      pre.id = 'pcw-debug-pre';
      pre.style.cssText = 'margin:0;white-space:pre-wrap;word-break:break-word;';
      debugOverlay.appendChild(pre);
      document.body.appendChild(debugOverlay);
    }
    const pre = debugOverlay.querySelector('#pcw-debug-pre');
    if (pre) pre.textContent = debugLines.join('\n');
  }

  if (DEBUG_ENABLED) {
    pcwDebug('Debug-Modus aktiv');
    pcwDebug('UA: ' + navigator.userAgent.slice(0, 80));
    window.addEventListener('beforeunload', () => pcwDebug('beforeunload — Page navigiert'));
    window.addEventListener('pagehide', () => pcwDebug('pagehide — Page verlassen'));
  }

  const dataEl = $(`#${sectionId}-data`);
  if (!dataEl) return;
  const produkte = JSON.parse(dataEl.textContent);

  const gateways = produkte
    .filter(p => p.rolle === 'gateway')
    .sort((a, b) => a.sortierung - b.sortierung);

  // Portal-Pakete sind die Varianten des Portal-Produkts (rolle 'plan', je ein
  // Eintrag pro Variante mit eigener variantId + handle-Schlüssel).
  const portalTiers = produkte
    .filter(p => p.rolle === 'plan')
    .sort((a, b) => a.sortierung - b.sortierung);

  const dienste = produkte
    .filter(p => p.rolle !== 'gateway' && p.rolle !== 'plan')
    .sort((a, b) => a.sortierung - b.sortierung);

  // --- Geräte-Katalog ---
  // Leitet aus den Gateway-Varianten die drei empfehlbaren Gerätetypen ab:
  //   batt = Batterie-Gerät (autark, immer mit SIM)
  //   ns   = Netzteil-Gerät mit SIM
  //   nw   = Netzteil-Gerät ohne SIM (Anbindung über WLAN)
  //
  // Zwei Produktmodelle werden unterstützt, weil die Umstellung schrittweise läuft:
  //   NEU  — Produkt = Gehäuseart, Variante = Stromversorgung × Anschluss. Erkennung
  //          läuft dann je Variante über den Stromversorgung-Wert.
  //   ALT  — Produkt = Stromversorgung, Variante = Gehäuse × Anschluss. Dort gibt es
  //          keinen Stromversorgung-Wert; „Produkt ohne Anschluss-Option" = Batterie.
  // Ausschlaggebend ist, ob die Variante einen Stromversorgung-Wert mitbringt. Damit
  // funktionieren alte und neue Produkte gleichzeitig, auch im Mischbetrieb.
  const norm = (/** @type {any} */ s) => String(s == null ? '' : s).trim().toLowerCase();

  // „exkl. SIM" enthält ebenfalls „sim" — deshalb wird der Ausschluss VOR dem
  // SIM-Treffer geprüft. Beide Schreibweisen kommen im Shop vor („WLAN", „exkl. SIM").
  /**
   * @param {any} anschluss
   * @returns {'nw' | 'ns' | null}
   */
  function connectionType(anschluss) {
    const a = norm(anschluss);
    if (!a) return null;
    if (a.includes('wlan') || a.includes('exkl') || a.includes('ohne')) return 'nw';
    if (a.includes('sim')) return 'ns';
    return null;
  }

  /** @param {any} stromversorgung */
  function isBatterie(stromversorgung) {
    const s = norm(stromversorgung);
    return s.includes('batt') || s.includes('akku');
  }

  // Signatur für den Lager-Fallback: welche Varianten gelten als dieselbe Ware?
  // Unit Separator als Trenner, damit kein Options-Wert die Grenze verwischen kann.
  const SIG_SEP = '\u001f';
  /** @param {any} pr */
  function variantSignature(pr) {
    return [norm(pr && pr.stromversorgung), norm(pr && pr.anschluss)].join(SIG_SEP);
  }

  function buildDeviceCatalog() {
    /** @type {Record<string, any[]>} */
    const byHandle = {};
    gateways.forEach(g => { (byHandle[g.productHandle] = byHandle[g.productHandle] || []).push(g); });

    /** @type {any[]} */ const battList = [];
    /** @type {any[]} */ const nsList = [];
    /** @type {any[]} */ const nwList = [];
    Object.keys(byHandle).forEach(handle => {
      const list = byHandle[handle];

      // NEUES Modell: die Stromversorgung steckt in der Variante.
      if (list.some(g => norm(g.stromversorgung) !== '')) {
        list.forEach(g => {
          if (isBatterie(g.stromversorgung)) { battList.push(g); return; }
          const t = connectionType(g.anschluss);
          if (t === 'nw') nwList.push(g);
          else if (t === 'ns') nsList.push(g);
        });
        return;
      }

      // ALTES Modell: die Stromversorgung steckt im Produkt.
      if (!list.some(g => norm(g.anschluss) !== '')) { battList.push(...list); return; }
      list.forEach(g => {
        const t = connectionType(g.anschluss);
        if (t === 'nw') nwList.push(g);
        else if (t === 'ns') nsList.push(g);
      });
    });

    const mk = (/** @type {any[]} */ list, /** @type {string} */ label, /** @type {string} */ icon, /** @type {string} */ desc) => {
      // Je Gehäuse eine Variante vorhalten, damit die Gehäusewahl des Kunden eine konkrete
      // Variant-ID bekommt. Bei mehreren Kandidaten je Gehäuse gewinnt die verfügbare —
      // hier ist das unbedenklich, weil das Gehäuse dasselbe bleibt.
      /** @type {Record<string, any>} */
      const byHousing = {};
      list.forEach(g => {
        const key = housingKey(g.gehaeuse);
        if (!key) return;
        const current = byHousing[key];
        if (!current || (current.available === false && g.available !== false)) byHousing[key] = g;
      });

      // „Auf Anfrage" ist eine Eigenschaft der WARE, nicht des Lagerbestands — der
      // Warenkorb-Schritt kann sie deshalb nicht durch einen Gehäuse-Tausch heilen.
      // Trägt irgendein Kandidat dieses Gerätetyps das Flag und bleibt gleichzeitig
      // keiner übrig, der verkäuflich ist (verfügbar UND nicht auf Anfrage), dann gibt
      // es für den Typ nichts zu verkaufen — unabhängig davon, welches Gehäuse die
      // Auflösung gerade trifft. Genau dieser Fall trat bei „Netzteil · WLAN" auf: das
      // Flag hing am Industrie-Gerät, die Auflösung landete über HOUSING_DEFAULT aber
      // beim ausverkauften Wohnbereich-Gerät ohne Flag — und damit im Warenkorb.
      //
      // Reines Ausverkauft ohne Flag bleibt ausdrücklich unberührt: dafür ist weiterhin
      // der Warenkorb-Schritt mit Ersatz-Rückfrage und Sellout-Meldung zuständig.
      const anyInquire = list.some(g => g.inquire === true || g.not_purchasable === true);
      const anySellable = list.some(g => g.available !== false && g.inquire !== true && g.not_purchasable !== true);
      const groupInquire = anyInquire && !anySellable;

      return {
        label, icon, desc,
        found: list.length > 0,
        groupInquire,
        byHousing,
        // Rückfall ohne gepflegtes Gehäuse: die ERSTE Variante in Datenreihenfolge, bewusst
        // OHNE Rücksicht auf Verfügbarkeit. Früher gewann hier die erste verfügbare Variante —
        // und weil der Katalog produktübergreifend gruppiert, hat das bei ausverkauftem
        // Wohnbereich still auf Industrie umgeschaltet, bevor Allocator und Rückfrage-Dialog
        // überhaupt beteiligt waren. Über einen Ersatz entscheidet ausschließlich der
        // Warenkorb-Schritt, weil nur der ihn auch meldet.
        any: list[0] || null,
      };
    };

    const cat = {
      nw: mk(nwList, ASSIST.devNw || 'Netzteil · WLAN', ICON_WIFI, ASSIST.devNwDesc || 'Netzstrom + Anbindung über Ihr WLAN'),
      ns: mk(nsList, ASSIST.devNs || 'Netzteil · SIM', ICON_SIM, ASSIST.devNsDesc || 'Netzstrom, autark über Mobilfunk'),
      batt: mk(battList, ASSIST.devBatt || 'Batterie · SIM', ICON_BATT, ASSIST.devBattDesc || 'Autark, ohne Steckdose & WLAN'),
    };
    // Fehlt ein Typ, fallen alle Immobilien mit dieser Empfehlung lautlos aus Berechnung
    // und Warenkorb (effectiveType → null). Deshalb hier laut werden: die Ursache ist immer
    // eine Fehlkonfiguration im Admin, nie eine Kundeneingabe.
    if (!cat.nw.found) console.warn('[PCW] Kein Netzteil·WLAN-Gateway gefunden (Anschluss „WLAN" bzw. „exkl. SIM").');
    if (!cat.ns.found) console.warn('[PCW] Kein Netzteil·SIM-Gateway gefunden (Anschluss „inkl. SIM").');
    if (!cat.batt.found) console.warn('[PCW] Kein Batterie-Gateway gefunden (Stromversorgung „Batterie" bzw. Produkt ohne Anschluss-Option).');

    // Mehrere Gateway-Produkte, aber auf keinem ein gepflegtes Gehäuse: dann greift nur der
    // Rückfall auf den Produktnamen. Der Ersatz funktioniert, die Rückfrage-Texte lesen sich
    // aber wie Produkttitel statt wie Gehäusebezeichnungen — und die Gehäuse-Frage im
    // Assistenten bleibt aus, weil es keine saubere Auswahl anzubieten gibt.
    const handleCount = Object.keys(byHandle).length;
    if (handleCount > 1 && !gateways.some(g => g.gehaeuseGepflegt === true)) {
      console.warn(
        `[PCW] ${handleCount} Gateway-Produkte, aber auf keinem das Metafeld kalkulator.gehaeuse. ` +
        'Ersatz und Rückfrage laufen über den Produktnamen als Notbehelf. ' +
        'Für saubere Texte je Produkt „Wohnbereich" bzw. „Industrie" hinterlegen.'
      );
    }
    return cat;
  }
  const deviceCatalog = buildDeviceCatalog();

  // Empfohlener Gerätetyp einer Immobilie (identisch zur Design-Vorlage 1B).
  function recType(p) {
    if (!p) return null;
    if (p.s === false) return 'batt';
    if (p.s === true) {
      if (p.w === true) return 'nw';
      if (p.w === false) return 'ns';
    }
    return null;
  }
  // Datensammler je Immobilie: 1 Gerät pro WE_PRO_DS Wohneinheiten (aufgerundet, min. 1).
  function dsCountFor(we) { return Math.max(1, Math.ceil((Number(we) || 1) / WE_PRO_DS)); }

  // Wählbare Gehäuse für die Assistenten-Frage, abgeleitet aus den Produktdaten
  // (Reihenfolge = Collection-Reihenfolge). Einmal berechnet, die Produktdaten sind ein
  // Snapshot des Seitenaufrufs.
  //
  // Nur GEPFLEGTE Gehäuse landen hier: fehlt das Metafeld, trägt das Feld als Notbehelf den
  // Produktnamen — der taugt zum Erkennen und Melden eines Ersatzes, aber nicht als Auswahl,
  // die man einem Kunden hinstellt.
  const HOUSING_OPTIONS = (() => {
    /** @type {string[]} */
    const seen = [];
    gateways.forEach(g => {
      if (g.gehaeuseGepflegt !== true) return;
      const raw = g.gehaeuse == null ? '' : String(g.gehaeuse).trim();
      if (!raw) return;
      if (!seen.some(h => housingKey(h) === housingKey(raw))) seen.push(raw);
    });
    return seen;
  })();

  // Muss der Kunde das Gehäuse wählen? Nur wenn eingeschaltet UND es überhaupt etwas zu
  // wählen gibt — bei einem einzigen Gehäuse wäre die Frage eine Sackgasse mit einer Antwort.
  const ASK_HOUSING = HOUSING_ENABLED && HOUSING_OPTIONS.length > 1;

  function defaultHousing() {
    if (!HOUSING_OPTIONS.length) return '';
    return HOUSING_OPTIONS.find(h => housingKey(h) === housingKey(HOUSING_DEFAULT)) || HOUSING_OPTIONS[0];
  }

  // Gehäuse einer Immobilie: die Wahl des Kunden, sonst das Standard-Gehäuse.
  function housingFor(p) {
    if (ASK_HOUSING && p && p.g) return p.g;
    return defaultHousing();
  }

  /**
   * Löst (Gerätetyp, Gehäuse) auf eine konkrete Variante auf.
   *   1. genau das gewünschte Gehäuse — AUCH wenn ausverkauft. Der Kunde hat es gewählt;
   *      über einen Ersatz entscheidet er im Warenkorb-Dialog, nicht der Katalog stillschweigend.
   *   2. das Standard-Gehäuse
   *   3. housing-agnostisch die erste verfügbare Variante (kein Gehäuse gepflegt)
   * @param {string | null} type
   * @param {string} [housing]
   */
  function deviceFor(type, housing) {
    const cat = type ? deviceCatalog[type] : null;
    if (!cat) return null;
    const g = (housing ? cat.byHousing[housingKey(housing)] : null)
      || cat.byHousing[housingKey(HOUSING_DEFAULT)]
      || cat.any;
    if (!g) return null;
    // cat.groupInquire greift, wenn der ganze Gerätetyp nichts Verkäufliches mehr hat
    // (siehe buildDeviceCatalog). Sonst entscheidet allein die aufgelöste Variante.
    const inquire = g.inquire === true || cat.groupInquire === true;
    return {
      label: cat.label,
      icon: cat.icon,
      desc: cat.desc,
      variantId: g.variantId,
      preis: typeof g.variantPreis === 'number' ? g.variantPreis : (g.preis_einheit || 0),
      cartExcluded: g.not_purchasable === true || inquire,
      inquire,
      info: g.info || '',
      gehaeuse: g.gehaeuse == null ? '' : String(g.gehaeuse).trim(),
    };
  }

  // Anzeigename: das Gehäuse nur nennen, wenn der Kunde es auch gewählt hat. Sonst bliebe
  // die Ausgabe für alle bestehenden Shops unverändert.
  function deviceLabel(dev) {
    if (!dev) return '';
    return ASK_HOUSING && dev.gehaeuse ? `${dev.label} (${dev.gehaeuse})` : dev.label;
  }

  // Ist der empfohlene Gerätetyp wirklich kaufbar? Ohne auflösbare Variante hat der Store
  // für diesen Anschlusstyp nichts im Angebot (Fehlkonfiguration, siehe console.warn oben) —
  // dann kann berechne() weder Position noch Warenkorbzeile bilden.
  function typeFulfillable(type, housing) {
    const dev = deviceFor(type, housing);
    return !!(dev && dev.variantId);
  }
  // „Effektiver" Gerätetyp fürs Zählen/Anzeigen/Bepreisen: die reine Empfehlung (recType),
  // aber nur wenn sie auch kaufbar ist. Damit bleiben Empfehlungs-Kasten, Summenkarte,
  // Ergebnisliste, Warenkorb und Preis konsistent. recType selbst bleibt reine s/w-Logik,
  // sodass „beantwortet?" (isAnswered) davon unberührt bleibt.
  function effectiveType(p) {
    const t = recType(p);
    return t && typeFulfillable(t, housingFor(p)) ? t : null;
  }

  // Vollständig beantwortet: Stromversorgung/WLAN — und das Gehäuse, sofern gefragt wird.
  function isAnswered(p) {
    if (!recType(p)) return false;
    if (ASK_HOUSING && !(p && p.g)) return false;
    return true;
  }

  // Preselection (and inheritance from the previous property) means a property is
  // technically answered the moment it exists — a whole portfolio could be walked past
  // without unfolding a single card. So with more than one property the card must have
  // been open once before it counts as done. Not needed for a single property (its card
  // is open from the start) nor in bulk mode (one card speaks for the whole set).
  function visitRequired() {
    return state.properties.length > 1 && !isBulkMode();
  }

  // Answered AND confirmed by the customer. This is the gate for "Weiter", the green
  // check on a collapsed row and the summary line; isAnswered stays the pure data check.
  function isComplete(p) {
    return isAnswered(p) && (!visitRequired() || !!(p && p.seen));
  }

  // Gruppen für Anzeige und Warenkorb: je Gerätetyp UND Gehäuse eine Zeile, weil beide
  // Gehäuse eigene Varianten (und im neuen Modell eigene Produkte) sind.
  function deviceBreakdown() {
    /** @type {Map<string, {type: string, housing: string, count: number}>} */
    const groups = new Map();
    state.properties.forEach(p => {
      const type = effectiveType(p);
      if (!type) return;
      const dev = deviceFor(type, housingFor(p));
      if (!dev) return;
      const key = type + SIG_SEP + housingKey(dev.gehaeuse);
      const entry = groups.get(key) || { type, housing: dev.gehaeuse, count: 0 };
      entry.count += dsCountFor(p.we);
      groups.set(key, entry);
    });
    // Stabile Reihenfolge: Gerätetyp wie bisher, innerhalb dessen alphabetisch nach Gehäuse.
    const order = ['nw', 'ns', 'batt'];
    return [...groups.values()].sort((a, b) =>
      (order.indexOf(a.type) - order.indexOf(b.type)) || a.housing.localeCompare(b.housing, 'de')
    );
  }
  function pendingCount() {
    return state.properties.filter(p => !isComplete(p)).length;
  }

  // Bulk mode shows ONE card for the whole set. If the properties disagree, that card
  // cannot display an answer — so the wizard must not walk on with a combination the
  // customer never saw (step 3 would quietly price two device types). This is NOT the
  // same as "properties are unanswered": each may well carry its own answer, they just
  // are not the same one. Hence its own message instead of a made-up pending count.
  function bulkNeedsAnswer() {
    return isBulkMode() && pendingCount() === 0 && !isAnswered(bulkProperty());
  }

  // --- State ---
  const TOTAL_STEPS = 3;
  const STORAGE_KEY = 'pcw-state';

  const defaultState = {
    currentStep: 1,
    maxVisitedStep: 1,
    vorname: '',
    nachname: '',
    email: '',
    // Geführter Assistent (Schritt 1): Anzahl Immobilien + Datensatz je Immobilie.
    // properties[i] = { s: Steckdose (true|false|null), w: WLAN (true|false|null),
    //   g: Gehäuse (string|null), we: Wohneinheiten }. Der Gerätetyp wird aus s/w
    //   abgeleitet (recType), das Gehäuse wählt der Kunde nur bei aktivierter Frage.
    immoCount: START_OBJEKTE,
    properties: Array.from({ length: START_OBJEKTE }, () => makeProperty()),
    openIndex: 0,
    // OMS-Voraussetzung: null (noch nicht beantwortet) | 'ja' | 'nein' | 'unsicher'.
    // 'nein'/'unsicher' sperren Schritt 1 (Hinweis-Kasten, wie WE-Obergrenze).
    omsStatus: null,
    selectedPortalVariantId: null,
    selectedDiensteIds: [],
    ansicht: 'monatlich',
  };

  // Spreading defaultState alone would hand out its live properties array, so every
  // "state" would share the same objects. Build the per-property records fresh.
  function freshState() {
    return {
      ...defaultState,
      properties: Array.from({ length: START_OBJEKTE }, () => makeProperty()),
      selectedDiensteIds: [],
    };
  }

  let state = freshState();

  // --- DOM refs ---
  const elSteps = $$('.pcw__step', root);
  const elStepBtns = $$('.pcw__step-btn', root);
  const elConnectors = $$('.pcw__step-connector', root);
  const elStepNum = $('[data-pcw-stepnum]', root);
  const elStepLabelMobile = $('[data-pcw-steplabel]', root);
  const stepLabels = elStepBtns.map(b => (b.getAttribute('aria-label') || '').trim());

  const elBtnNext = $(`#${sectionId}-btn-next`);
  const elBtnPrev = $(`#${sectionId}-btn-prev`);
  const elBtnKaufen = $(`#${sectionId}-btn-kaufen`);
  const elBtnAngebot = $(`#${sectionId}-btn-angebot`);
  const elBtnPdf = $(`#${sectionId}-btn-pdf`);
  const elBtnPdfWrap = $$('[data-pcw-pdf-wrap]', root);
  const elBtnNeu = $(`#${sectionId}-btn-neu`);
  const elActions = $(`#${sectionId}-actions`);
  const elAngebotWrap = $$('[data-pcw-angebot-wrap]', root);
  const elInquire = $(`#${sectionId}-inquire`);
  const elInquireBtn = $(`#${sectionId}-inquire-btn`);
  const elAssistant = $(`#${sectionId}-assistant`);
  const elSummary = $(`#${sectionId}-summary`);
  const elStep1Alert = $(`#${sectionId}-step1-alert`);
  const elWeLimit = $(`#${sectionId}-we-limit`);
  const elWeLimitBtn = $(`#${sectionId}-we-limit-btn`);
  const elOmsWarning = $(`#${sectionId}-oms-warning`);
  const elOmsBtns = $$('.pcw__oms-btn', root);
  const elOmsTexts = $$('[data-oms-text]', root);
  const elTierWarning = $(`#${sectionId}-tier-warning`);
  const elResultBody = $(`#${sectionId}-result-body`);
  const elResultSummary = $(`#${sectionId}-result-summary`);
  // Ergebnis-Layout 3F: die untere Betonung zeigt die LAUFENDEN Kosten (nicht mehr die
  // kombinierte Erst-Monats-Summe); Einmalkosten stehen als Akzent-Zeile in der Tabelle.
  const elTotalValue = $(`#${sectionId}-total-value`);
  const elTotalAmount = $(`#${sectionId}-total-amount`);
  const elTotalPeriod = $(`#${sectionId}-total-period`);
  const elLaufendBox = $(`#${sectionId}-laufend-box`);
  const elEmailStatus = $(`#${sectionId}-email-status`);
  const elImmo = $(`#${sectionId}-immo`);
  const elImmoVal = $(`#${sectionId}-immo-val`);

  // --- Helpers ---
  function formatPreis(betrag) {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(betrag);
  }

  // Ersetzt den Zahl-Platzhalter in (editierbaren) Assistent-Texten. Zwei Schreibweisen
  // werden akzeptiert: {n} in den Schema-Defaults und Editor-Texten, %n überall dort, wo
  // geschweifte Klammern nicht gesetzt werden dürfen (Liquid-Fallbacks in dieser Datei).
  function fmtCount(tpl, n) { return String(tpl).replace(/\{n\}|%n/, String(n)); }

  function clampInt(v, min, max) {
    v = parseInt(v, 10);
    if (isNaN(v)) v = min;
    return Math.max(min, Math.min(max, v));
  }

  // Zählt den Gesamtpreis beim Einblenden von 0 auf den Zielwert hoch (Zähler-Optik).
  // Respektiert prefers-reduced-motion (setzt sofort den Endwert).
  function animateCountUp(el, target) {
    if (!el) return;
    const zielwert = Number(target) || 0;
    if (prefersReducedMotion || zielwert <= 0) {
      el.textContent = formatPreis(zielwert);
      return;
    }
    const duration = 650;
    let startTs = null;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    function frame(ts) {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      el.textContent = formatPreis(zielwert * ease(progress));
      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        el.textContent = formatPreis(zielwert);
      }
    }
    requestAnimationFrame(frame);
  }

  // HTML-sicheres Escapen für per innerHTML eingefügte Texte (z.B. Info-Tooltips).
  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  // Info-Tooltip (ⓘ) für eine Ergebnis-Position in Schritt 3 – gleiche Optik wie
  // die Zusatzoptionen in Schritt 2.
  function infoButtonHtml(text) {
    const safe = escapeHtml(text).replace(/\n/g, '<br>');
    return `<button type="button" class="pcw__result-info" aria-label="Mehr Infos">
      <svg viewBox="0 0 16 16" width="13" height="13" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.4"></circle>
        <path d="M8 7.2v3.6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></path>
        <circle cx="8" cy="4.8" r="0.95" fill="currentColor"></circle>
      </svg>
      <span class="pcw__result-info-popup" role="tooltip">${safe}</span>
    </button>`;
  }

  function getGesamtWE() {
    return state.properties.reduce((sum, p) => sum + (Number(p.we) || 0), 0);
  }

  function getGwMenge() {
    // Summe der Datensammler über alle beantworteten UND kaufbaren Immobilien-Empfehlungen.
    return state.properties.reduce((sum, p) => sum + (effectiveType(p) ? dsCountFor(p.we) : 0), 0);
  }

  // Gesamtzahl Datensammler rein aus der WE-Anzahl (unabhängig vom Gerätetyp) — steht fest,
  // sobald eine Immobilie existiert, auch bevor Steckdose/WLAN beantwortet sind. Nur für die
  // Anzeige „Benötigte Datensammler". Warenkorb/Preis nutzen weiter getGwMenge() (nur
  // beantwortete, kaufbare Immobilien).
  function getDsCountTotal() {
    return state.properties.reduce((sum, p) => sum + dsCountFor(p.we), 0);
  }

  // Liegt die Gesamt-Wohneinheitenzahl über der konfigurierten Obergrenze?
  function isWeOverLimit() {
    return MAX_WE > 0 && getGesamtWE() > MAX_WE;
  }

  // Antwort mit Hinweis-Kasten: „Nein" / „Weiß ich nicht" — unabhängig davon, ob sie sperrt.
  function isOmsWarned() {
    return state.omsStatus === 'nein' || state.omsStatus === 'unsicher';
  }

  // OMS-Voraussetzung nicht erfüllt UND die Antwort ist im Editor als Sperre gesetzt.
  // Ohne Sperre bleibt nur der Hinweis stehen und der Kunde darf weiterrechnen.
  function isOmsBlocked() {
    if (state.omsStatus === 'nein') return OMS_BLOCK.nein;
    if (state.omsStatus === 'unsicher') return OMS_BLOCK.unsicher;
    return false;
  }

  // Schritt 1 ist gesperrt, solange die WE-Obergrenze überschritten ist ODER die
  // OMS-Voraussetzung nicht bestätigt wurde.
  function isStep1Blocked() {
    return isWeOverLimit() || isOmsBlocked();
  }

  // Blendet die Hinweis-Kästen (WE-Obergrenze + OMS) ein/aus und versteckt in Schritt 1
  // den "Weiter"-Button, solange eine Voraussetzung nicht erfüllt ist.
  function updateGates() {
    if (elWeLimit) elWeLimit.hidden = !isWeOverLimit();
    // Der Hinweis-Kasten hängt an der Antwort, nicht an der Sperre: auch wer weiterrechnen
    // darf, soll den Hinweis zur OMS-Fähigkeit lesen. Text je Antwort.
    if (elOmsWarning) elOmsWarning.hidden = !isOmsWarned();
    elOmsTexts.forEach(el => { el.hidden = el.dataset.omsText !== state.omsStatus; });
    // OMS „nein"/„unsicher": Weiter-Button sichtbar lassen und stattdessen die Hinweiszeile
    // am Fuß von Schritt 1 zeigen (dasselbe Feld wie bei offenen Immobilien) — den Button
    // kommentarlos verschwinden zu lassen hat Nutzer verwirrt.
    if (isOmsBlocked()) {
      showStep1Alert(ASSIST.omsBlockedAlert || 'Bitte bestätigen Sie die OMS-Fähigkeit Ihrer Zähler, um fortzufahren.');
    }
    if (state.currentStep === 1 && elBtnNext) {
      // Nur die WE-Obergrenze versteckt den Button (ihr Kasten bietet den Kontakt-Button).
      elBtnNext.hidden = isWeOverLimit();
    }
  }

  // OMS-Frage aktiv (im Editor gepflegt), aber noch nicht beantwortet? Dann darf Schritt 1
  // nicht verlassen werden. Ist die Frage ausgeblendet (oms_question leer → keine Buttons
  // gerendert), gibt es nichts zu erzwingen — sonst würde der Rechner ohne OMS-Frage sperren.
  function isOmsUnanswered() {
    return elOmsBtns.length > 0 && state.omsStatus == null;
  }
  // Darf Schritt 1 nach vorn verlassen werden? Harte Gates (WE / OMS „nein"·„unsicher") frei,
  // OMS-Frage beantwortet, alle Immobilien abgeschlossen. Steuert Kreis-Sperre + goToStep.
  function canLeaveStep1() {
    return !isStep1Blocked() && !isOmsUnanswered() && pendingCount() === 0 && !bulkNeedsAnswer();
  }
  // Rückt die OMS-Frage in den Blick, wenn „Weiter" ohne Auswahl geklickt wird — die Meldung
  // sitzt am Fuß von Schritt 1, die Frage ganz oben.
  function scrollOmsIntoView() {
    const el = $('.pcw__oms', root);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function saveState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_) { /* ignore */ }
  }

  function loadState() {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        Object.assign(state, parsed);
        // Ältere gespeicherte Stände kennen das Gehäuse-Feld nicht. Ohne Auffüllen wäre
        // p.g undefined und eine laufende Sitzung würde bei aktivierter Frage in einem
        // „nicht beantwortet" hängen, das der Kunde nicht zuordnen kann.
        if (Array.isArray(state.properties)) {
          state.properties.forEach(p => { if (p && p.g === undefined) p.g = null; });
        }
        return true;
      }
    } catch (_) { /* ignore */ }
    return false;
  }

  // --- Step Navigation ---
  function goToStep(n) {
    if (n < 1 || n > TOTAL_STEPS) return;

    // Schritt 1 gesperrt: WE-Obergrenze überschritten ODER OMS nicht bestätigt.
    // Schritte > 1 bleiben dann unerreichbar, der User wird in Schritt 1 gehalten.
    if (n > 1 && isStep1Blocked()) {
      pcwDebug(`Schritt ${n} blockiert — ${isWeOverLimit() ? `WE-Obergrenze (${MAX_WE})` : 'OMS-Voraussetzung'}`);
      n = 1;
    } else if (n > 1 && isOmsUnanswered()) {
      // OMS-Frage noch nicht beantwortet: nicht nach vorn springen, Hinweis wie beim „Weiter".
      pcwDebug(`Schritt ${n} blockiert — OMS-Frage unbeantwortet`);
      showStep1Alert(ASSIST.omsAlert || 'Bitte beantworten Sie zuerst die Frage zur OMS-Fähigkeit.');
      n = 1;
    } else if (n > 1 && pendingCount() > 0) {
      // Offene Immobilien: nicht nach vorn springen — berechne()/getGwMenge() würden sie
      // stillschweigend überspringen, sodass Preis/Warenkorb Immobilien verlieren. In
      // Schritt 1 halten + Hinweis. Der Alert überlebt das folgende updateStep1(), weil
      // hideStep1Alert() dort nur bei pendingCount()===0 greift.
      const offen = pendingCount();
      pcwDebug(`Schritt ${n} blockiert — ${offen} Immobilie(n) offen`);
      showStep1Alert(pendingAlertText(offen), 'pending');
      n = 1;
    } else if (n > 1 && bulkNeedsAnswer()) {
      pcwDebug(`Schritt ${n} blockiert — Sammel-Karte ohne gemeinsame Angabe`);
      showStep1Alert(BULK_MIXED_ALERT);
      n = 1;
    }

    // Bug-Fix: Schritt 3 nur erreichbar nach erfolgreichem Submit für die aktuellen Werte.
    // Nur relevant, wenn das Kontakt-Gate aktiv ist.
    if (CONTACT_GATE && n === 3 && !notificationSentForCurrentValues) {
      pcwDebug('Schritt 3 blockiert — Werte geändert, neue Berechnung erforderlich');
      showEmailStatus('Optionen wurden geändert. Bitte erneut auf "Berechnung anzeigen & senden" klicken.', true);
      n = 2;
      setTimeout(() => {
        const submitBtn = root.querySelector('.pcw__notify-submit-btn');
        submitBtn?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }

    state.currentStep = n;
    if (n > state.maxVisitedStep) state.maxVisitedStep = n;

    elSteps.forEach(s => {
      s.hidden = parseInt(s.dataset.step, 10) !== n;
    });

    updateStepIndicator(n);

    elBtnPrev.hidden = n === 1;
    const isLastStep = n === TOTAL_STEPS;
    // "Weiter" bei Schritt 3 verstecken (dort gibt es die Aktions-Buttons). In Schritt 2
    // nur verstecken, wenn das Kontakt-Gate aktiv ist (dann übernimmt der Captcha-Submit-
    // Button). Ohne Gate bleibt "Weiter" sichtbar und führt direkt zum Ergebnis.
    elBtnNext.hidden = isLastStep || (n === 2 && CONTACT_GATE);

    if (n === 1) updateStep1();
    if (n === 2) {
      updateTierRecommendation();
      // Captcha-/Kontaktformular nur einrichten, wenn das Gate aktiv ist.
      if (CONTACT_GATE) {
        initCaptchaWidget();
        prefillNotifyForm();
        // Auto-Focus auf das erste leere Feld (oder Email falls alle gefüllt).
        setTimeout(() => {
          const elNotifyName = $(`#${sectionId}-notify-name`);
          const elNotifyEmail = $(`#${sectionId}-notify-email`);
          let target = null;
          if (elNotifyName && !elNotifyName.value.trim()) target = elNotifyName;
          else if (elNotifyEmail && !elNotifyEmail.value.trim()) target = elNotifyEmail;
          else target = elNotifyEmail;
          target?.focus({ preventScroll: false });
          pcwDebug('Auto-Focus auf ' + (target?.id || 'kein Feld'));
        }, 250);
      }
    }
    if (n === 3) {
      renderErgebnis();
    }

    animateStepEnter(n);

    saveState();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Aktualisiert die Step-Kreise + Verbinder im Hero-Kopf und die Mobile-Zeile.
  function updateStepIndicator(n) {
    elStepBtns.forEach(btn => {
      const num = parseInt(btn.dataset.step, 10);
      const active = num === n;
      const done = num < n;
      // Schritt 1 muss vollständig sein, bevor ein Kreis nach vorn führen darf: harte Gates
      // (WE / OMS „nein"·„unsicher"), unbeantwortete OMS-Frage oder offene Immobilien sperren.
      const forwardLocked = num > 1 && !canLeaveStep1();
      const clickable = num <= state.maxVisitedStep && num !== n && !forwardLocked;
      btn.classList.toggle('is-active', active);
      btn.classList.toggle('is-done', done);
      btn.classList.toggle('is-clickable', clickable);
      btn.disabled = !clickable;
      if (active) btn.setAttribute('aria-current', 'step');
      else btn.removeAttribute('aria-current');
      const circle = btn.querySelector('.pcw__step-circle');
      if (circle) circle.textContent = done ? '✓' : String(num);
    });
    // Verbinder i (0-basiert) sitzt hinter Schritt (i+1) → gefüllt wenn (i+1) < n.
    elConnectors.forEach((c, i) => {
      c.classList.toggle('is-done', (i + 1) < n);
    });
    if (elStepNum) elStepNum.textContent = String(n);
    if (elStepLabelMobile) elStepLabelMobile.textContent = stepLabels[n - 1] || '';
  }

  // Sanftes Einblenden des aktiven Schritts (retriggert die CSS-Animation).
  function animateStepEnter(n) {
    if (prefersReducedMotion) return;
    const activeStepEl = elSteps.find(s => parseInt(s.dataset.step, 10) === n);
    if (!activeStepEl) return;
    activeStepEl.classList.remove('is-entering');
    void activeStepEl.offsetWidth;
    activeStepEl.classList.add('is-entering');
  }

  function validateStep(n) {
    if (n === 1) {
      // WE-Obergrenze überschritten oder OMS nicht bestätigt → nicht weiter,
      // der jeweilige Hinweis-Kasten übernimmt.
      if (isStep1Blocked()) { updateGates(); return false; }
      // OMS-Voraussetzung muss beantwortet sein (sofern die Frage aktiv ist).
      if (isOmsUnanswered()) {
        showStep1Alert(ASSIST.omsAlert || 'Bitte beantworten Sie zuerst die Frage zur OMS-Fähigkeit.');
        scrollOmsIntoView();
        return false;
      }
      // Alle Immobilien müssen abgeschlossen sein (Steckdose — und ggf. WLAN — beantwortet).
      const pending = pendingCount();
      if (pending > 0) {
        showStep1Alert(pendingAlertText(pending), 'pending');
        flagPendingRows();
        return false;
      }
      if (bulkNeedsAnswer()) {
        showStep1Alert(BULK_MIXED_ALERT);
        flagPendingRows();
        return false;
      }
      hideStep1Alert();
      return true;
    }
    if (n === 2) {
      // Kein manueller Datensammler mehr — Gerät + Portal werden automatisch gesetzt.
      return true;
    }
    return true;
  }

  // --- Schritt 1: Geführter Assistent ---
  // Rendert Immobilien-Zeilen (Akkordeon) + dunkle Summenkarte und aktualisiert die Gates.
  function updateStep1() {
    renderAssistant();
    renderSummary();
    updateGates();
    // Step-Kreise nachziehen: Kommt in Schritt 1 eine offene Immobilie dazu (z. B. per
    // Slider), muss die Klickbarkeit der Vorwärts-Kreise sofort folgen — updateStep1 ist
    // der gemeinsame Chokepoint aller Schritt-1-Änderungen.
    updateStepIndicator(state.currentStep);
    // Bei OMS-Sperre bleibt die Hinweiszeile stehen (sie kommt aus updateGates), sonst
    // würde sie hier sofort wieder verschwinden.
    const stillPending = pendingCount();
    if (stillPending === 0 && !bulkNeedsAnswer() && !isOmsBlocked()) {
      hideStep1Alert();
    } else if (stillPending > 0 && elStep1Alert && !elStep1Alert.hidden && elStep1Alert.dataset.kind === 'pending') {
      // Keep the count truthful while the customer works through the cards.
      elStep1Alert.textContent = pendingAlertText(stillPending);
    }
  }

  // Passt die properties-Liste an die Immobilien-Anzahl an (bestehende Werte bleiben).
  function growProperties(n) {
    const list = state.properties.slice(0, n);
    while (list.length < n) list.push(makeProperty(list[list.length - 1]));
    state.properties = list;
  }

  // --- Bulk mode ---
  function isBulkMode() {
    return BULK_ENABLED && state.immoCount > BULK_THRESHOLD;
  }

  // Bounds of the combined units slider. Both ends are whole multiples of the
  // per-property grid (1, 1 + WE_STEP, 1 + 2·WE_STEP …), so every reachable total can be
  // split into per-property values that sit ON that grid — see applyTotalWe.
  // Largest grid value that still fits under the per-property maximum. snapWe() is the
  // wrong tool here: it rounds to the NEAREST step and then clamps, so it can hand back
  // MAX_WE_PRO_OBJEKT itself — and that value need not sit on the grid (120 with step 5
  // does not). Rounding down keeps the top of the bulk slider exactly divisible.
  function bulkStepMax() {
    return 1 + Math.floor((MAX_WE_PRO_OBJEKT - 1) / WE_STEP) * WE_STEP;
  }
  function bulkMin() { return state.properties.length; }
  function bulkMax() { return state.properties.length * bulkStepMax(); }

  // Spreads a total unit count evenly across all properties. Even distribution is not
  // cosmetic: every building needs its own data collector, and dsCountFor() derives that
  // count per property.
  //
  // The split happens in WE_STEP increments rather than single units, because the
  // per-property sliders only accept grid values. Distributing 100 over 8 as 13/12 would
  // leave thumb, value label and state disagreeing the moment the customer drops back
  // below the bulk threshold — exactly what snapWe() exists to prevent. Since both slider
  // ends are grid-aligned, the sum still comes out exact.
  function applyTotalWe(total) {
    const count = state.properties.length;
    if (!count) return;
    const target = clampInt(total, bulkMin(), bulkMax());
    // Units above the mandatory 1 per property, counted in grid steps.
    const stepsTotal = Math.round((target - count) / WE_STEP);
    const stepsPerProperty = Math.floor(stepsTotal / count);
    let rest = stepsTotal - stepsPerProperty * count;
    state.properties.forEach((p) => {
      const extra = rest > 0 ? 1 : 0;
      if (rest > 0) rest--;
      p.we = Math.min(bulkStepMax(), 1 + (stepsPerProperty + extra) * WE_STEP);
    });
  }

  // The whole set as one pseudo property. Answers that are not shared collapse to null,
  // so recType() yields no recommendation instead of promoting the first property's
  // answer to a claim about all of them.
  function bulkProperty() {
    return {
      s: commonAnswer('s'),
      w: commonAnswer('w'),
      g: commonAnswer('g'),
      we: getGesamtWE(),
    };
  }

  // Value shared by ALL properties, otherwise null — so a bulk chip never looks active
  // while the individual properties still disagree.
  function commonAnswer(/** @type {'s' | 'w' | 'g'} */ key) {
    const list = state.properties;
    if (!list.length) return null;
    const first = list[0][key];
    return list.every(p => p[key] === first) ? first : null;
  }

  function chipHtml(act, i, on, label) {
    return `<button type="button" class="pcw__chip${on ? ' is-on' : ''}" data-act="${act}" data-i="${i}" data-v="${label === 'Ja' ? '1' : '0'}">${label}</button>`;
  }

  // Gehäuse-Chip. Anders als Ja/Nein trägt der Wert einen frei gepflegten Text, deshalb
  // ein eigener Bauer statt chipHtml (das den Wert aus dem Label ableitet).
  function housingChipHtml(i, value, on) {
    return `<button type="button" class="pcw__chip${on ? ' is-on' : ''}" data-act="g" data-i="${i}" data-v="${escapeHtml(value)}">${escapeHtml(value)}</button>`;
  }

  // Empfehlungs-Kasten (Gerätetyp) für eine beantwortete Immobilie.
  function recBoxHtml(type, we, housing, countOverride) {
    const dev = deviceFor(type, housing);
    if (!dev) return '';
    const cnt = countOverride == null ? dsCountFor(we) : countOverride;
    return `<div class="pcw__rec">
      <span class="pcw__rec-icon">${dev.icon}</span>
      <span class="pcw__rec-text">
        <span class="pcw__rec-eyebrow">${escapeHtml(ASSIST.recEyebrow || 'Unsere Empfehlung')}</span>
        <span class="pcw__rec-title">${cnt}× ${escapeHtml(deviceLabel(dev))}</span>
        <span class="pcw__rec-desc">${escapeHtml(dev.desc)}</span>
      </span>
    </div>`;
  }

  function summaryLineFor(p) {
    if (!isComplete(p)) return ASSIST.pendingShort || 'noch offen';         // Immobilie noch nicht beantwortet
    const type = effectiveType(p);
    if (!type) return `${p.we || 0} WE`;             // beantwortet, aber kein kaufbares Gerät (Katalog unvollständig)
    const dev = deviceFor(type, housingFor(p));
    return `${dsCountFor(p.we)}× ${deviceLabel(dev)} · ${p.we || 0} WE`;
  }

  // Fill level of the combined units slider, whose range differs from the per-property one.
  function bulkFillPct(total) {
    const min = bulkMin();
    const max = bulkMax();
    const v = clampInt(total, min, max);
    return max > min ? ((v - min) / (max - min)) * 100 : 0;
  }

  // Füllstand des WE-Sliders in Prozent (identisch zur Immobilien-Slider-Optik).
  function weFillPct(we) {
    const v = Number(we) || 1;
    return MAX_WE_PRO_OBJEKT > 1 ? ((v - 1) / (MAX_WE_PRO_OBJEKT - 1)) * 100 : 0;
  }

  // Aktualisiert den Empfehlungs-Kasten der offenen Zeile IN PLACE (ohne Re-Render),
  // damit das Ziehen am WE-Slider nicht abbricht.
  function updateRecBox(i) {
    if (i !== state.openIndex || !elAssistant) return;
    const p = state.properties[i];
    const type = effectiveType(p);
    if (!type) return;
    const dev = deviceFor(type, housingFor(p));
    const titleEl = elAssistant.querySelector('.pcw__assist-row--open .pcw__rec-title');
    if (titleEl && dev) titleEl.textContent = `${dsCountFor(p.we)}× ${deviceLabel(dev)}`;
  }

  // Same as updateRecBox, but for the single bulk card: the count shown is the total
  // across all properties, not the one of a single row.
  function updateRecBoxBulk() {
    if (!elAssistant) return;
    const pseudo = bulkProperty();
    const type = effectiveType(pseudo);
    if (!type) return;
    const dev = deviceFor(type, housingFor(pseudo));
    const titleEl = elAssistant.querySelector('.pcw__assist-row--open .pcw__rec-title');
    if (titleEl && dev) titleEl.textContent = `${getDsCountTotal()}× ${deviceLabel(dev)}`;
  }

  // Merkt den zuletzt gerenderten offenen Index: die Einblende-Animation läuft NUR,
  // wenn tatsächlich eine andere Immobilie geöffnet wird — nicht bei jeder
  // Options-Änderung (sonst „ploppt" die Karte bei jedem Klick, siehe Nutzer-Feedback).
  let prevOpenIndex = -1;

  function renderAssistant() {
    if (!elAssistant) return;
    const qS = ASSIST.qSteckdose || 'Steckdose / Netzstrom vorhanden?';
    const qW = ASSIST.qWlan || 'WLAN vorhanden?';
    const labelWe = ASSIST.labelWe || 'Wohneinheiten';
    const battNote = ASSIST.battNote || '';
    const infoS = ASSIST.infoSteckdose ? infoButtonHtml(ASSIST.infoSteckdose) : '';
    const infoW = ASSIST.infoWlan ? infoButtonHtml(ASSIST.infoWlan) : '';
    const qG = ASSIST.qGehaeuse || 'Wo wird der Datensammler montiert?';
    const infoG = ASSIST.infoGehaeuse ? infoButtonHtml(ASSIST.infoGehaeuse) : '';
    // WE-Label: Info-Tooltip (Standardtext leitet sich aus der DS-Abdeckung ab).
    const infoWeText = ASSIST.infoWe || `Ein Datensammler erfasst bis zu ${WE_PRO_DS} Wohneinheiten.`;
    const infoWe = infoWeText ? infoButtonHtml(infoWeText) : '';
    const count = state.properties.length;
    const appearClass = state.openIndex !== prevOpenIndex ? ' pcw__assist-row--appear' : '';

    // Bulk mode: one card for all properties. The questions write to every property, and
    // a single slider carries the combined unit count.
    if (isBulkMode()) {
      // The single card stands for the whole set, so rendering it confirms all of them.
      state.properties.forEach(p => { if (p) p.seen = true; });
      const s = commonAnswer('s');
      const w = commonAnswer('w');
      const g = commonAnswer('g');
      const total = getGesamtWE();
      const bulkWlan = s === true
        ? `<div class="pcw__assist-q">
            <div class="pcw__assist-q-label">${escapeHtml(qW)}${infoW}</div>
            <div class="pcw__chips">
              ${chipHtml('w', 'all', w === true, 'Ja')}
              ${chipHtml('w', 'all', w === false, 'Nein')}
            </div>
          </div>`
        : '';
      const bulkBattNote = (s === false && battNote)
        ? `<div class="pcw__assist-note">${escapeHtml(battNote)}</div>`
        : '';
      const bulkHousing = ASK_HOUSING
        ? `<div class="pcw__assist-q">
            <div class="pcw__assist-q-label">${escapeHtml(qG)}${infoG}</div>
            <div class="pcw__chips pcw__chips--housing">
              ${HOUSING_OPTIONS.map(h => housingChipHtml('all', h, housingKey(g) === housingKey(h))).join('')}
            </div>
          </div>`
        : '';
      const pseudo = bulkProperty();
      const bulkType = effectiveType(pseudo);
      const bulkRec = bulkType ? recBoxHtml(bulkType, total, housingFor(pseudo), getDsCountTotal()) : '';
      const bulkTitle = fmtCount(BULK_TITLE, count);
      const done = isAnswered(pseudo);

      elAssistant.innerHTML = `<div class="pcw__assist-row pcw__assist-row--open${done ? ' pcw__assist-row--open-complete' : ''}">
        <span class="pcw__assist-title">${escapeHtml(bulkTitle)}${done ? `<span class="pcw__assist-title-check" aria-hidden="true">${ICON_CHECK}</span>` : ''}</span>
        ${BULK_NOTE ? `<div class="pcw__assist-note">${escapeHtml(fmtCount(BULK_NOTE, count))}</div>` : ''}
        <div class="pcw__assist-q">
          <div class="pcw__assist-q-label">${escapeHtml(qS)}${infoS}</div>
          <div class="pcw__chips">
            ${chipHtml('s', 'all', s === true, 'Ja')}
            ${chipHtml('s', 'all', s === false, 'Nein')}
          </div>
        </div>
        ${bulkWlan}
        ${bulkBattNote}
        ${bulkHousing}
        <div class="pcw__slider pcw__slider--we">
          <div class="pcw__slider-head">
            <span class="pcw__we-label-wrap">
              <label class="pcw__slider-label" for="${sectionId}-we-all">${escapeHtml(BULK_LABEL_WE)}</label>${infoWe}
            </span>
            <span class="pcw__slider-value" id="${sectionId}-we-val-all">${total}</span>
          </div>
          <input
            class="pcw__range pcw__assist-we-range"
            type="range"
            id="${sectionId}-we-all"
            data-bulk="1"
            min="${bulkMin()}"
            max="${bulkMax()}"
            step="${WE_STEP}"
            value="${total}"
            style="--pcw-fill:${bulkFillPct(total)}%"
            aria-describedby="${sectionId}-we-scale-all"
          >
          <div class="pcw__range-scale" id="${sectionId}-we-scale-all">
            <span>${bulkMin()}</span><span>${bulkMax()}</span>
          </div>
        </div>
        ${bulkRec}
      </div>`;
      prevOpenIndex = -1;
      return;
    }

    // Whatever card is rendered open has been shown to the customer.
    const openProp = state.properties[state.openIndex];
    if (openProp) openProp.seen = true;

    const html = state.properties.map((p, i) => {
      const title = `Immobilie ${i + 1}`;
      if (i !== state.openIndex) {
        // Eingeklappte Zeile → Zusammenfassungs-Button. Beantwortet = grün + Häkchen
        // (abgeschlossen), sonst „offen"-Markierung.
        const isDone = isComplete(p);
        const stateClass = isDone ? ' pcw__assist-row--complete' : ' pcw__assist-row--pending';
        const statusIcon = isDone
          ? `<span class="pcw__assist-status pcw__assist-status--done" aria-label="abgeschlossen">${ICON_CHECK}</span>`
          : `<span class="pcw__assist-status pcw__assist-status--open" aria-hidden="true"></span>`;
        return `<button type="button" class="pcw__assist-row pcw__assist-row--done${stateClass}" data-act="open" data-i="${i}">
          ${statusIcon}
          <span class="pcw__assist-done-text">
            <span class="pcw__assist-done-title">${title}</span>
            <span class="pcw__assist-done-sub">${escapeHtml(summaryLineFor(p))}</span>
          </span>
          <span class="pcw__assist-edit" aria-hidden="true">${isDone ? 'Ändern' : 'Öffnen'}</span>
        </button>`;
      }

      const type = isAnswered(p);
      const wlanBlock = p.s === true
        ? `<div class="pcw__assist-q">
            <div class="pcw__assist-q-label">${escapeHtml(qW)}${infoW}</div>
            <div class="pcw__chips">
              ${chipHtml('w', i, p.w === true, 'Ja')}
              ${chipHtml('w', i, p.w === false, 'Nein')}
            </div>
          </div>`
        : '';
      const battBlock = (p.s === false && battNote)
        ? `<div class="pcw__assist-note">${escapeHtml(battNote)}</div>`
        : '';
      const housingBlock = ASK_HOUSING
        ? `<div class="pcw__assist-q">
            <div class="pcw__assist-q-label">${escapeHtml(qG)}${infoG}</div>
            <div class="pcw__chips pcw__chips--housing">
              ${HOUSING_OPTIONS.map(h => housingChipHtml(i, h, housingKey(p.g) === housingKey(h))).join('')}
            </div>
          </div>`
        : '';
      const devType = effectiveType(p);
      const recBlock = devType ? recBoxHtml(devType, p.we, housingFor(p)) : '';
      const prevBtn = i > 0
        ? `<button type="button" class="pcw__btn pcw__btn--ghost pcw__assist-back" data-act="prev" data-i="${i}">&larr; Zurück</button>`
        : '';
      const nextBtn = i < count - 1
        ? `<button type="button" class="pcw__assist-nextbtn" data-act="next" data-i="${i}">Nächste Immobilie &rarr;</button>`
        : '';

      return `<div class="pcw__assist-row pcw__assist-row--open${type ? ' pcw__assist-row--open-complete' : ''}${appearClass}">
        <span class="pcw__assist-title">${title}${type ? `<span class="pcw__assist-title-check" aria-hidden="true">${ICON_CHECK}</span>` : ''}</span>
        <div class="pcw__assist-q">
          <div class="pcw__assist-q-label">${escapeHtml(qS)}${infoS}</div>
          <div class="pcw__chips">
            ${chipHtml('s', i, p.s === true, 'Ja')}
            ${chipHtml('s', i, p.s === false, 'Nein')}
          </div>
        </div>
        ${wlanBlock}
        ${battBlock}
        ${housingBlock}
        <div class="pcw__slider pcw__slider--we">
          <div class="pcw__slider-head">
            <span class="pcw__we-label-wrap">
              <label class="pcw__slider-label" for="${sectionId}-we-${i}">${escapeHtml(labelWe)}</label>${infoWe}
            </span>
            <span class="pcw__slider-value" id="${sectionId}-we-val-${i}">${p.we || 0}</span>
          </div>
          <input
            class="pcw__range pcw__assist-we-range"
            type="range"
            id="${sectionId}-we-${i}"
            data-i="${i}"
            min="1"
            max="${MAX_WE_PRO_OBJEKT}"
            step="${WE_STEP}"
            value="${p.we || 1}"
            style="--pcw-fill:${weFillPct(p.we)}%"
            aria-describedby="${sectionId}-we-scale-${i}"
          >
          <div class="pcw__range-scale" id="${sectionId}-we-scale-${i}">
            <span>${escapeHtml(WE_SCALE_MIN)}</span><span>${MAX_WE_PRO_OBJEKT}</span>
          </div>
        </div>
        ${recBlock}
        <div class="pcw__assist-nav">
          ${prevBtn}<span class="pcw__assist-nav-spacer"></span>${nextBtn}
        </div>
      </div>`;
    }).join('');

    elAssistant.innerHTML = html;
    prevOpenIndex = state.openIndex;
  }

  function renderSummary() {
    if (!elSummary) return;
    const chips = deviceBreakdown()
      .map(g => {
        const dev = deviceFor(g.type, g.housing);
        if (!dev) return '';
        return `<span class="pcw__sum-chip">${dev.icon}${g.count}× ${escapeHtml(deviceLabel(dev))}</span>`;
      }).join('');
    const pending = pendingCount();
    const over = MAX_WE > 0 && getGesamtWE() > MAX_WE;
    const title = ASSIST.devicesTitle || 'Benötigte Datensammler';

    elSummary.innerHTML = `
      <div class="pcw__sum-stats">
        <div class="pcw__sum-stat">
          <span class="pcw__sum-stat-label">${escapeHtml(title)}</span>
          <span class="pcw__sum-stat-value pcw__sum-stat-value--accent">${getDsCountTotal()}</span>
        </div>
        <div class="pcw__sum-stat">
          <span class="pcw__sum-stat-label">WE gesamt</span>
          <span class="pcw__sum-stat-value">${getGesamtWE()}</span>
        </div>
      </div>
      ${chips ? `<div class="pcw__sum-chips">${chips}</div>` : ''}
      ${pending > 0 ? `<span class="pcw__sum-note">${escapeHtml(fmtCount(ASSIST.pendingNote || 'Noch {n} Immobilie(n) offen', pending))}</span>` : ''}
      ${over ? `<span class="pcw__sum-over">Über ${MAX_WE} WE — wir erstellen ein individuelles Angebot.</span>` : ''}
    `;
  }

  // Zeigt/versteckt den Schritt-1-Hinweis „bitte alle Immobilien abschließen".
  // `kind` tags the message so a still-visible one can be refreshed in place (see updateStep1).
  function showStep1Alert(msg, kind) {
    if (!elStep1Alert) return;
    elStep1Alert.textContent = msg;
    elStep1Alert.hidden = false;
    elStep1Alert.dataset.kind = kind || '';
  }
  function hideStep1Alert() {
    if (elStep1Alert) {
      elStep1Alert.hidden = true;
      elStep1Alert.textContent = '';
      delete elStep1Alert.dataset.kind;
    }
  }
  function pendingAlertText(n) {
    return fmtCount(ASSIST.pendingAlert || 'Bitte schließen Sie zuerst alle Immobilien ab — noch {n} offen.', n);
  }

  // Hebt alle offenen Immobilien-Zeilen hervor und scrollt zur ersten (Feedback beim
  // blockierten „Weiter"). Die Klasse wird beim nächsten renderAssistant automatisch entfernt.
  function flagPendingRows() {
    if (!elAssistant) return;
    if (isBulkMode()) {
      const card = elAssistant.firstElementChild;
      if (!card) return;
      const pend = !isAnswered(bulkProperty());
      card.classList.toggle('pcw__assist-row--attention', pend);
      if (pend) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    let first = null;
    [...elAssistant.children].forEach((row, i) => {
      const pend = !isComplete(state.properties[i]);
      row.classList.toggle('pcw__assist-row--attention', pend);
      if (pend && !first) first = row;
    });
    if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // --- Slider ---
  function updateSliderFill(el, min, max) {
    if (!el) return;
    const v = Number(el.value);
    const pct = max > min ? ((v - min) / (max - min)) * 100 : 0;
    el.style.setProperty('--pcw-fill', pct + '%');
  }

  // Aktualisiert nur die Zahlen der Summenkarte in place (billig, für Drag-Frames).
  function updateSummaryLive() {
    if (!elSummary) return;
    const vals = elSummary.querySelectorAll('.pcw__sum-stat-value');
    if (vals.length >= 2) {
      vals[0].textContent = String(getDsCountTotal());
      vals[1].textContent = String(getGesamtWE());
    }
  }

  // Ein Frame Sync des WE-Sliders (Wert/Füllung/Empfehlung/Summenzahlen) — liest den
  // Wert direkt aus range.value (auch ohne 'input'-Event immer aktuell).
  function syncWeFromRange(range) {
    if (range.dataset.bulk === '1') {
      applyTotalWe(range.value);
      const totalEl = document.getElementById(`${sectionId}-we-val-all`);
      if (totalEl) totalEl.textContent = String(getGesamtWE());
      updateSliderFill(range, bulkMin(), bulkMax());
      updateRecBoxBulk();
      updateSummaryLive();
      return;
    }
    const i = parseInt(range.dataset.i, 10);
    const p = state.properties[i];
    if (!p) return;
    p.we = clampInt(range.value, 1, MAX_WE_PRO_OBJEKT);
    const valEl = document.getElementById(`${sectionId}-we-val-${i}`);
    if (valEl) valEl.textContent = String(p.we);
    updateSliderFill(range, 1, MAX_WE_PRO_OBJEKT);
    updateRecBox(i);
    updateSummaryLive();
  }

  // Teureres Commit (volle Summenkarte inkl. Chips + Gates + Speichern) — nur bei
  // Drag-Ende bzw. Einzeländerungen, nicht pro Frame.
  function commitWe() {
    renderSummary();
    updateGates();
    notificationSentForCurrentValues = false;
    saveState();
  }

  // Aktiver Pointer-Drag am WE-Slider? Dann zeichnet die rAF-Schleife (unten), und der
  // 'input'-Handler committet nicht doppelt.
  let weDragging = false;

  // --- Assistent: Immobilien-Anzahl + Zeilen-Interaktion ---
  function setImmoCount(n) {
    n = clampInt(n, 1, MAX_OBJEKTE);
    state.immoCount = n;
    growProperties(n);
    if (state.openIndex >= n) state.openIndex = n - 1;
    if (elImmoVal) elImmoVal.textContent = String(n);
    updateSliderFill(elImmo, 1, MAX_OBJEKTE);
    updateStep1();
    notificationSentForCurrentValues = false;
    saveState();
  }

  // Klicks in den (per innerHTML neu gerenderten) Zeilen laufen über Event-Delegation.
  function setupAssistant() {
    if (!elAssistant) return;
    elAssistant.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-act]');
      if (!btn || !elAssistant.contains(btn)) return;
      const i = parseInt(btn.dataset.i, 10);
      const act = btn.dataset.act;
      if (act === 'open') { state.openIndex = i; updateStep1(); saveState(); return; }
      if (act === 'prev') { state.openIndex = Math.max(0, i - 1); updateStep1(); saveState(); return; }
      if (act === 'next') { state.openIndex = Math.min(state.properties.length - 1, i + 1); updateStep1(); saveState(); return; }

      // In bulk mode one chip answers for every property at once.
      const targets = btn.dataset.i === 'all'
        ? state.properties
        : [state.properties[i]].filter(Boolean);
      if (!targets.length) return;
      if (act === 's') {
        const hasSocket = btn.dataset.v === '1';
        targets.forEach(p => {
          p.s = hasSocket;
          if (!hasSocket) p.w = null; // ohne Steckdose keine WLAN-Frage
        });
      } else if (act === 'w') {
        const hasWlan = btn.dataset.v === '1';
        targets.forEach(p => { p.w = hasWlan; });
      } else if (act === 'g') {
        const housing = btn.dataset.v || null;
        targets.forEach(p => { p.g = housing; });
      } else {
        return;
      }
      updateStep1();
      notificationSentForCurrentValues = false;
      saveState();
    });

    // WE-Slider zeichnet die Füllung über --pcw-fill, das bisher NUR im 'input'-Handler
    // gesetzt wurde. Chrome verschluckt 'input' beim schnellen Ziehen aber oft (feuert nur
    // sporadisch) → Füllung/Wert bleiben stehen, während der Thumb weiterläuft. Fix:
    // Während eines Pointer-Drags per requestAnimationFrame JEDEN Frame direkt aus
    // range.value neu zeichnen (range.value ist auch ohne 'input'-Event immer aktuell).
    elAssistant.addEventListener('pointerdown', (e) => {
      const range = e.target.closest('.pcw__assist-we-range');
      if (!range || !elAssistant.contains(range)) return;
      weDragging = true;
      let rafId = requestAnimationFrame(function loop() {
        syncWeFromRange(range);
        if (weDragging) rafId = requestAnimationFrame(loop);
      });
      const stop = () => {
        weDragging = false;
        cancelAnimationFrame(rafId);
        syncWeFromRange(range); // finaler Sync auf den Loslass-Wert
        commitWe();
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
      };
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    });

    // Fallback für Tastatur/Klick (kein Pointer-Drag). Feuert 'input' während eines Drags
    // doch, macht es nur den billigen Sync — das Commit übernimmt dann das Loslassen.
    elAssistant.addEventListener('input', (e) => {
      const range = e.target.closest('.pcw__assist-we-range');
      if (!range || !elAssistant.contains(range)) return;
      syncWeFromRange(range);
      if (!weDragging) commitWe();
    });
  }

  // --- OMS-Voraussetzung ---
  function applyOmsSelectionClasses() {
    elOmsBtns.forEach(btn => {
      const on = btn.dataset.oms === state.omsStatus;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function setupOms() {
    elOmsBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        state.omsStatus = btn.dataset.oms;
        applyOmsSelectionClasses();
        updateGates();
        // Beantwortete OMS-Frage gibt die Vorwärts-Kreise wieder frei.
        updateStepIndicator(state.currentStep);
        // „Ja" räumt die Meldung ab; „nein"/„unsicher" zeigt via updateGates die Sperr-Hinweiszeile.
        if (!isOmsBlocked()) hideStep1Alert();
        notificationSentForCurrentValues = false;
        saveState();
      });
    });
  }

  // --- Portal Tier ---
  const TIER_BASIS = cfg.tierBasis || 'we';
  const TIER_UNIT_LABEL = cfg.tierUnitLabel || 'WE';
  const TIER_MIN_PROP = TIER_BASIS === 'objekte' ? 'immo_min' : 'we_min';
  const TIER_MAX_PROP = TIER_BASIS === 'objekte' ? 'immo_max' : 'we_max';

  function getTierVergleichswert() {
    return TIER_BASIS === 'objekte' ? state.immoCount : getGesamtWE();
  }

  function getTierMin(tier) {
    return tier ? (tier[TIER_MIN_PROP] || 0) : 0;
  }

  function getTierMax(tier) {
    return tier ? (tier[TIER_MAX_PROP] || 0) : 0;
  }

  function bestimmeOptimalerTier(vergleichswert) {
    const sorted = [...portalTiers].sort((a, b) => getTierMin(a) - getTierMin(b));
    const match = sorted.find(p =>
      vergleichswert >= getTierMin(p) && (getTierMax(p) === 0 || vergleichswert <= getTierMax(p))
    );
    if (match) return match;
    if (sorted.length === 0) return null;
    if (vergleichswert < getTierMin(sorted[0])) return sorted[0];
    return sorted[sorted.length - 1];
  }

  function findTierByVariantId(variantId) {
    return portalTiers.find(t => t.variantId === variantId) || null;
  }

  // --- WE-Staffel ---
  // Volume pricing for a portal package: instead of a flat price the package is billed
  // per unit, and the rate drops at configured thresholds (metafield kalkulator.we_staffel
  // on the variant). The staffel is FLAT, not progressive: the reached tier's rate applies
  // to every unit — 12 WE at the "from 11" rate of 0,69 € make 8,28 €, not a mix of rates.
  // Packages without the metafield keep their flat price and every existing code path.

  /**
   * Staffel rows of a tier, ascending by threshold. Rows without a usable price are
   * dropped so a typo in the metafield cannot turn the package free.
   * @param {any} tier
   */
  function staffelRows(tier) {
    const rows = tier && Array.isArray(tier.we_staffel) ? tier.we_staffel : [];
    return rows
      .map(r => ({ ab: Number(r && r.ab) || 0, preis: Number(r && r.preis) }))
      .filter(r => Number.isFinite(r.preis) && r.preis > 0)
      .sort((a, b) => a.ab - b.ab);
  }

  /**
   * Matching staffel row for a unit count, or null when the package is not staffed.
   * Below the lowest threshold the lowest row still applies: the package is only
   * recommended from its we_min upwards, and a manual pick must not end up unpriced.
   * @param {any} tier
   * @param {number} einheiten / units
   */
  function staffelRow(tier, einheiten) {
    const rows = staffelRows(tier);
    if (!rows.length) return null;
    let match = rows[0];
    rows.forEach(r => { if (einheiten >= r.ab) match = r; });
    return match;
  }

  // Staffelpreise auf den Karten mitziehen: Der Betrag hängt an der WE-Zahl aus Schritt 1,
  // die Karte wird aber serverseitig ohne diese Zahl gerendert.
  function updateTierPrices() {
    const vergleichswert = getTierVergleichswert();
    $$('.pcw__tier-card', root).forEach(card => {
      const radio = $('input[type="radio"]', card);
      if (!radio) return;
      const tier = findTierByVariantId(parseInt(radio.value, 10));
      const row = staffelRow(tier, vergleichswert);
      if (!row) return;
      // Unterhalb der kleinsten Stufe gilt deren Satz als Untergrenze (siehe staffelRow),
      // die Stufe ist aber nicht erreicht — dann wird auch keine Zeile hervorgehoben.
      const erreicht = vergleichswert >= row.ab;
      const valueEl = $('[data-tier-price-value]', card);
      const unitEl = $('[data-tier-price-unit]', card);
      if (valueEl) valueEl.textContent = formatPreis(row.preis * vergleichswert);
      if (unitEl) unitEl.textContent = '/ Monat';
      $$('[data-staffel-ab]', card).forEach(el => {
        el.classList.toggle('is-active', erreicht && parseInt(el.dataset.staffelAb, 10) === row.ab);
      });
    });
  }

  function findTierByHandle(handle) {
    return portalTiers.find(t => t.handle === handle) || null;
  }

  function getMinRequiredTier() {
    let highest = null;
    $$('.pcw__option input[type="checkbox"]:checked', root).forEach(cb => {
      const reqHandle = cb.dataset.minTierHandle;
      if (!reqHandle) return;
      const reqTier = findTierByHandle(reqHandle);
      if (!reqTier) return;
      if (!highest || getTierMin(reqTier) > getTierMin(highest)) {
        highest = reqTier;
      }
    });
    return highest;
  }

  function updateTierAvailability() {
    const vergleichswert = getTierVergleichswert();
    $$('.pcw__tier-card', root).forEach(card => {
      const radio = $('input[type="radio"]', card);
      const tier = findTierByVariantId(parseInt(radio.value, 10));
      const tierMax = getTierMax(tier);
      const tooSmall = tierMax > 0 && vergleichswert > tierMax;
      card.classList.toggle('is-disabled', tooSmall);
      radio.disabled = tooSmall;
    });
  }

  function updateTierRecommendation() {
    const vergleichswert = getTierVergleichswert();
    const optimal = bestimmeOptimalerTier(vergleichswert);

    if (!optimal) return;

    updateTierAvailability();
    updateTierPrices();

    const tierCards = $$('.pcw__tier-card', root);

    $$('.pcw__tier-badge', root).forEach(b => b.remove());

    tierCards.forEach(card => {
      const radio = $('input[type="radio"]', card);
      if (parseInt(radio.value, 10) === optimal.variantId) {
        const badge = document.createElement('span');
        badge.className = 'pcw__tier-badge';
        badge.textContent = 'Empfohlen';
        card.prepend(badge);
      }
    });

    // Auto-Upgrade wenn aktueller Tier zu klein geworden ist
    const currentTier = findTierByVariantId(state.selectedPortalVariantId);
    const currentMax = getTierMax(currentTier);
    const currentTooSmall = currentMax > 0 && vergleichswert > currentMax;

    if (!state.selectedPortalVariantId || currentTooSmall) {
      selectTier(optimal.variantId);
    } else {
      syncIncludedOptions();
    }

    checkTierUpgrade();
  }

  function selectTier(variantId) {
    const tierCards = $$('.pcw__tier-card', root);
    tierCards.forEach(card => {
      const radio = $('input[type="radio"]', card);
      const isTarget = parseInt(radio.value, 10) === variantId;
      card.classList.toggle('is-selected', isTarget);
      radio.checked = isTarget;
    });
    state.selectedPortalVariantId = variantId;
    syncIncludedOptions();
    saveState();
  }

  // Hakt alle Dienste an, deren included_from_handle einen Tier benennt
  // dessen tier_min <= aktuellem Tier ist.
  function syncIncludedOptions() {
    const currentTier = findTierByVariantId(state.selectedPortalVariantId);
    if (!currentTier) return;
    const currentMin = getTierMin(currentTier);

    $$('.pcw__option input[type="checkbox"]', root).forEach(cb => {
      if (cb.dataset.comingSoon === '1') return;

      const includedFrom = cb.dataset.includedFromHandle;
      if (!includedFrom) return;

      const includedFromTier = findTierByHandle(includedFrom);
      if (!includedFromTier) return;

      const includedFromMin = getTierMin(includedFromTier);
      if (currentMin >= includedFromMin) {
        if (!cb.checked) {
          cb.checked = true;
          syncDiensteState();
        }
      }
    });
  }

  function setupTiers() {
    $$('.pcw__tier-card', root).forEach(card => {
      const radio = $('input[type="radio"]', card);
      card.addEventListener('click', () => {
        if (card.classList.contains('is-disabled')) return;
        selectTier(parseInt(radio.value, 10));
        checkTierUpgrade();
        notificationSentForCurrentValues = false;
      });
    });
  }

  // Ausklappbare Leistungs-Liste je Paket-Karte (mobil eingeklappt).
  function setupFeatureToggles() {
    $$('.pcw__tier-feat-toggle', root).forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest('.pcw__tier-card');
        if (!card) return;
        const open = card.classList.toggle('is-feat-open');
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  }

  function checkTierUpgrade() {
    if (!elTierWarning) return;

    const currentTier = findTierByVariantId(state.selectedPortalVariantId);
    const currentMin = getTierMin(currentTier);

    const requiredTier = getMinRequiredTier();
    if (requiredTier && currentMin < getTierMin(requiredTier)) {
      elTierWarning.textContent =
        `Die gewählten Optionen (Telefonsupport/API-Zugriff) erfordern mindestens das ${requiredTier.beschriftung}-Paket. Das Paket wurde automatisch angepasst.`;
      elTierWarning.classList.add('is-visible');
      selectTier(requiredTier.variantId);
      return;
    }

    const vergleichswert = getTierVergleichswert();
    const optimal = bestimmeOptimalerTier(vergleichswert);
    if (optimal && state.selectedPortalVariantId !== optimal.variantId) {
      if (currentMin < getTierMin(optimal)) {
        elTierWarning.textContent =
          `Für ${vergleichswert} ${TIER_UNIT_LABEL} empfehlen wir mindestens das ${optimal.beschriftung}-Paket.`;
        elTierWarning.classList.add('is-visible');
        return;
      }
    }

    elTierWarning.classList.remove('is-visible');
    elTierWarning.textContent = '';
  }

  // --- Dienste / Options ---
  function setupDienste() {
    $$('.pcw__option-info', root).forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });

    $$('.pcw__option input[type="checkbox"]', root).forEach(cb => {
      if (cb.dataset.comingSoon === '1') {
        cb.addEventListener('click', (e) => {
          e.preventDefault();
          cb.checked = false;
        });
        return;
      }
      cb.addEventListener('change', () => {
        syncDiensteState();
        checkTierUpgrade();
        notificationSentForCurrentValues = false;
      });
    });
  }

  function syncDiensteState() {
    state.selectedDiensteIds = $$('.pcw__option input[type="checkbox"]:checked', root)
      .map(cb => parseInt(cb.value, 10));
    saveState();
  }

  function findProductByVariantId(variantId) {
    return produkte.find(p => p.variantId === variantId) || null;
  }


  function isProductCartExcluded(variantId) {
    const p = findProductByVariantId(variantId);
    if (!p) return false;
    return p.not_purchasable === true || p.inquire === true;
  }

  function isProductInquire(variantId) {
    const p = findProductByVariantId(variantId);
    return p ? p.inquire === true : false;
  }

  // --- Calculation ---
  function berechne() {
    const positionen = [];
    const gwMenge = getGwMenge();
    const gesamtWE = getGesamtWE();

    // Datensammler: je Gerätetyp UND Gehäuse eine Position mit aggregierter Menge. Das
    // Gehäuse muss die Gruppierung mitbestimmen, weil es eine eigene Variante (im neuen
    // Produktmodell sogar ein eigenes Produkt) mit eigener Variant-ID ist. Über einen
    // Ersatz bei Ausverkauf entscheidet der Kunde beim Warenkorb-Klick.
    deviceBreakdown().forEach(({ type, housing, count }) => {
      const dev = deviceFor(type, housing);
      if (!dev || !dev.variantId) return;
      positionen.push({
        label: `${deviceLabel(dev)} × ${count}`,
        menge: count,
        betrag: dev.preis * count,
        periode: 'einmalig',
        variantId: dev.variantId,
        cartExcluded: dev.cartExcluded,
        inquire: dev.inquire === true,
        info: dev.info || '',
      });
    });

    const selectedDiensteEls = $$('.pcw__option input[type="checkbox"]:checked', root);

    selectedDiensteEls.forEach(cb => {
      const preis = parseFloat(cb.dataset.preis);
      const basis = cb.dataset.basis;
      const periode = cb.dataset.periode;
      const vid = parseInt(cb.value, 10);

      let menge = 1;
      let mengeHinweis = '';
      if (basis === 'we') {
        menge = gesamtWE;
        mengeHinweis = ` (${gesamtWE} WE)`;
      } else if (basis === 'gateway' || basis === 'datensammler') {
        menge = gwMenge;
        mengeHinweis = ` × ${gwMenge}`;
      }

      let posPeriode = 'einmalig';
      if (periode === 'monatlich') posPeriode = 'monatlich';
      else if (periode === 'jaehrlich') posPeriode = 'jaehrlich';
      const betrag = preis * menge;

      positionen.push({
        label: cb.dataset.beschriftung + mengeHinweis,
        menge,
        betrag,
        periode: posPeriode,
        variantId: vid,
        cartExcluded: isProductCartExcluded(vid),
        inquire: isProductInquire(vid),
        info: findProductByVariantId(vid)?.info || '',
      });
    });

    const tierRadio = $('input[name="portal_tier"]:checked', root);
    if (tierRadio) {
      const vid = parseInt(tierRadio.value, 10);
      // Staffel-Paket: Preis je Einheit × Einheiten statt Pauschalpreis.
      //
      // Ein Staffel-Paket ist ZWINGEND „auf Anfrage": der Shopify-Variantenpreis ist fix,
      // ein mengenabhängiger Paketpreis wäre im Warenkorb also falsch (dort käme der
      // Pauschalpreis an). Das Flag hängt deshalb an der Staffel selbst und nicht am
      // Metafeld custom.inquire_product — sonst würde ein vergessenes Häkchen im Admin
      // einen sichtbaren Preis erzeugen, den der Checkout nicht einhält. Angezeigt und
      // mitsummiert wird der Betrag trotzdem, siehe preisBekannt / hatPreis().
      const staffel = staffelRow(findTierByVariantId(vid), getTierVergleichswert());
      const staffelHinweis = staffel
        ? ` (${getTierVergleichswert()} ${TIER_UNIT_LABEL} × ${formatPreis(staffel.preis)})`
        : '';
      positionen.push({
        label: `Portal-Paket: ${tierRadio.dataset.beschriftung}${staffelHinweis}`,
        menge: 1,
        betrag: staffel ? staffel.preis * getTierVergleichswert() : parseFloat(tierRadio.dataset.preis),
        // Bekannter Preis trotz „auf Anfrage": das Paket ist noch nicht kaufbar, der
        // Betrag steht aber fest und gehört in Anzeige, Summen und Angebots-PDF.
        preisBekannt: staffel != null,
        periode: 'monatlich',
        variantId: vid,
        // PayWhirl-Abo: Selling-Plan-ID aus dem Liquid-Data-Attribut.
        sellingPlanId: tierRadio.dataset.sellingPlan ? parseInt(tierRadio.dataset.sellingPlan, 10) : null,
        cartExcluded: isProductCartExcluded(vid),
        inquire: isProductInquire(vid) || staffel != null,
        info: findProductByVariantId(vid)?.info || '',
      });
    }

    // Einrichtungsgebühr: einmalige Pflicht-Position, wird bei JEDEM Kauf 1× mitgelegt.
    const setupFee = cfg.setupFee;
    if (setupFee && setupFee.variantId) {
      positionen.push({
        label: setupFee.title || 'Einrichtungsgebühr',
        menge: 1,
        betrag: Number(setupFee.price) || 0,
        periode: 'einmalig',
        variantId: setupFee.variantId,
        cartExcluded: false,
        inquire: false,
        info: setupFee.info || '',
      });
    }

    return positionen;
  }

  // --- Ansicht-Toggle (Monatlich/Jährlich) ---
  function syncToggleUI() {
    const container = $('.pcw__toggle', root);
    if (container) container.classList.toggle('is-jaehrlich', state.ansicht === 'jaehrlich');
    $$('.pcw__toggle-btn', root).forEach(b => {
      const on = b.dataset.ansicht === state.ansicht;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function setupResultToggle() {
    $$('.pcw__toggle-btn', root).forEach(btn => {
      btn.addEventListener('click', () => {
        state.ansicht = btn.dataset.ansicht;
        syncToggleUI();
        renderErgebnis();
        saveState();
      });
    });
  }

  function setInquireMode(on) {
    if (elInquire) elInquire.hidden = !on;
    if (elActions) elActions.hidden = on;
    // Quote link would duplicate the inquire CTA, so it goes away in inquire mode
    elAngebotWrap.forEach(el => { el.hidden = on; });
  }

  function hasInquirePosition(positionen) {
    return (positionen || berechne()).some(p => p.inquire === true);
  }

  // „Auf Anfrage" blendet den Betrag aus — außer bei Positionen mit bekanntem Preis
  // (Staffel-Pakete). Die sind nicht kaufbar, ihr Betrag steht aber fest und darf
  // deshalb angezeigt und mitsummiert werden.
  /** @param {any} p */
  const hatPreis = (p) => p.inquire !== true || p.preisBekannt === true;

  // --- Render Results ---
  function renderErgebnis() {
    const positionen = berechne();
    const einmaligRows = positionen.filter(p => p.periode === 'einmalig');
    const recurringRows = positionen.filter(p => p.periode === 'monatlich' || p.periode === 'jaehrlich');

    const istMonatlich = state.ansicht === 'monatlich';
    // Jede laufende Position auf die gewählte Ansicht normalisieren.
    const normBetrag = (p) => {
      if (p.periode === 'monatlich') return istMonatlich ? p.betrag : p.betrag * 12;
      return istMonatlich ? p.betrag / 12 : p.betrag; // jaehrlich
    };

    const preisWirksam = hatPreis;
    const einmalig = einmaligRows.filter(preisWirksam).reduce((s, p) => s + p.betrag, 0);
    const laufend = recurringRows.filter(preisWirksam).reduce((s, p) => s + normBetrag(p), 0);

    setInquireMode(hasInquirePosition(positionen));

    // Without line items the PDF would be an empty sheet, so hide the download.
    // The "on request" case stays visible: that is where the sheet is most useful.
    elBtnPdfWrap.forEach(el => { el.hidden = positionen.length === 0; });

    syncToggleUI();

    // Zusammenfassung in der Navy-Bar oben.
    if (elResultSummary) {
      elResultSummary.textContent =
        `${state.immoCount} Immobilie(n) · ${getGesamtWE()} WE · ${getGwMenge()} Datensammler`;
    }

    // Untere Betonung = LAUFENDE Kosten (Option 3F). Der Umschalter wirkt nur hier;
    // die Einmalkosten stehen als Akzent-Zeile in der Tabelle.
    if (elLaufendBox) elLaufendBox.hidden = recurringRows.filter(preisWirksam).length === 0;
    if (elTotalValue) {
      elTotalValue.dataset.value = String(laufend);
      animateCountUp(elTotalAmount || elTotalValue, laufend);
    }
    if (elTotalPeriod) {
      elTotalPeriod.textContent = istMonatlich ? '/ Monat' : '/ Jahr';
    }

    // Betrags-Spalte einer Position: Preis, oder Ersatztext bei „auf Anfrage".
    const betragHtml = (p, wert) => (hatPreis(p)
      ? `<strong>${formatPreis(wert)}</strong>`
      : `<span class="pcw__result-inquire">${escapeHtml(INQUIRE_LABEL)}</span>`);

    // Positions-Gruppen
    let html = '';
    if (einmaligRows.length) {
      html += `<div>
        <div class="pcw__result-group-title">Einmalige Kosten</div>
        ${einmaligRows.map(p => `
          <div class="pcw__result-row">
            <span class="pcw__result-label">${p.label}${p.info ? infoButtonHtml(p.info) : ''}</span>
            ${betragHtml(p, p.betrag)}
          </div>`).join('')}
        <div class="pcw__result-accent-row">
          <span class="pcw__result-accent-label">Einmalkosten <span class="pcw__result-accent-note">(einmalig)</span></span>
          <strong>${formatPreis(einmalig)}</strong>
        </div>
      </div>`;
    }
    if (recurringRows.length) {
      html += `<div>
        <div class="pcw__result-group-title">Laufende Kosten · ${istMonatlich ? 'monatlich' : 'jährlich'}</div>
        ${recurringRows.map(p => `
          <div class="pcw__result-row">
            <span class="pcw__result-label">${p.label}${p.info ? infoButtonHtml(p.info) : ''}</span>
            ${betragHtml(p, normBetrag(p))}
          </div>`).join('')}
      </div>`;
    }

    if (elResultBody) elResultBody.innerHTML = html;
  }

  // --- Cart API ---
  /**
   * variantSig = die Ausprägung, die das GERÄT ausmacht: Stromversorgung + Anschluss
   * (z. B. „Batterie" + „inkl. SIM"). Diese Kombination darf beim Inventar-Fallback NICHT
   * getauscht werden — ein Batterie-Gerät gegen ein Netzteil-Gerät zu ersetzen wäre ein
   * anderer Artikel, nicht dieselbe Ware in anderer Verpackung. Alle übrigen Options-Werte
   * (Gehäuse Wohnbereich/Industrie, Farbe …) sind aus Kundensicht dasselbe Gerät und dürfen
   * beim Ausverkauf gemischt werden (z. B. 1× Wohnbereich + 4× Industrie).
   * Fehlen beide Optionen (altes Modell: Batterie-Produkt mit nur einer Gehäuse-Option),
   * ist die Signatur leer → ALLE Varianten des Produkts sind untereinander austauschbar.
   *
   * Der Gehäuse-Tausch läuft über zwei Modelle: früher war das Gehäuse ein Options-Wert
   * innerhalb eines Produkts, künftig sind Wohnbereich und Industrie zwei Produkte. Beide
   * Fälle werden über das normalisierte Feld `gehaeuse` abgebildet — der Tausch ist damit
   * unabhängig davon, ob er innerhalb eines Produkts oder produktübergreifend passiert.
   * Er geschieht NIE still: addToCart fragt vorher mit konkreten Stückzahlen nach.
   *
   * WICHTIG: Der Lagerbestand kommt aus dem serverseitig gerenderten Datenblock (Liquid
   * variant.inventory_quantity/-policy), NICHT aus /products/*.js — der Storefront-AJAX-
   * Endpoint liefert seit ~2023 keine Mengen mehr (nur noch `available`). Nur so kann der
   * Fallback anteilig mischen, statt nur „ganz verfügbar / ganz ausverkauft" zu kennen.
   * @typedef {{id:number, available:number, availableBool:boolean, policy:string, tracked:boolean, title:string, handle:string, variantSig:string, gehaeuse:string}} PcwVariantInfo
   */

  // Baut Verfügbarkeits-Maps aus den serverseitigen Produktdaten (produkte) — kein
  // Netzwerk-Fetch. Stand: Seitenaufruf (Snapshot); echte Restmengen erzwingt zusätzlich
  // Shopify beim /cart/add.js (422-Backstop).
  //   byVariant   variantId → Info
  //   byHandle    productHandle → alle Gateway-Varianten des Produkts
  //   bySignature variantSig → Gateway-Varianten ALLER Produkte, die ein Gehäuse tragen.
  //               Das ist die Grundlage für den produktübergreifenden Gehäuse-Tausch: seit
  //               Wohnbereich und Industrie eigene Produkte sind, liegen die Geschwister
  //               nicht mehr unter demselben Handle.
  // Erfasst werden ALLE Gateway-Varianten, nicht nur die der aktuellen Positionen — ein
  // Geschwister-Gehäuse steckt per Definition in einem Produkt, das gar nicht gewählt wurde.
  function buildAvailability() {
    /** @type {Map<number, PcwVariantInfo>} */
    const byVariant = new Map();
    /** @type {Map<string, PcwVariantInfo[]>} */
    const byHandle = new Map();
    /** @type {Map<string, PcwVariantInfo[]>} */
    const bySignature = new Map();

    produkte
      .filter((/** @type {any} */ pr) => pr.rolle === 'gateway')
      .forEach((/** @type {any} */ pr) => {
        const tracked = !!pr.inventoryManagement && pr.inventoryManagement !== '';
        const handle = pr.productHandle;
        const info = {
          id: pr.variantId,
          // Getrackte Restmenge (Liquid), sonst unbegrenzt.
          available: tracked && typeof pr.inventoryQuantity === 'number' ? pr.inventoryQuantity : Infinity,
          availableBool: pr.available !== false,
          policy: pr.inventoryPolicy || 'deny',
          tracked,
          title: pr.title,
          handle,
          // Stromversorgung + Anschluss fixieren die Signatur (siehe Typedef).
          variantSig: variantSignature(pr),
          gehaeuse: pr.gehaeuse == null ? '' : String(pr.gehaeuse).trim(),
        };
        byVariant.set(pr.variantId, info);

        if (!byHandle.has(handle)) byHandle.set(handle, []);
        (byHandle.get(handle) || []).push(info);

        if (info.gehaeuse) {
          if (!bySignature.has(info.variantSig)) bySignature.set(info.variantSig, []);
          (bySignature.get(info.variantSig) || []).push(info);
        }
      });

    return { byVariant, byHandle, bySignature };
  }

  /** @param {PcwVariantInfo=} info */
  function variantVerfuegbar(info) {
    if (!info) return Infinity;
    if (info.availableBool === false) return 0;
    // Nur bei aktiver Bestandsführung + „nicht überverkaufen" (deny) begrenzt die Menge.
    if (info.tracked && info.policy === 'deny' && Number.isFinite(info.available)) {
      return Math.max(0, info.available);
    }
    return Infinity;
  }

  /**
   * Verteilt die benötigte Menge auf konkrete Varianten. Reihenfolge ist die ganze Logik:
   *   1. die gewählte Variante selbst
   *   2. weitere Varianten desselben Gehäuses (gleiche Signatur)
   *   3. erst dann Geschwister-Gehäuse (gleiche Signatur, anderes Gehäuse, evtl. anderes Produkt)
   * Dadurch wird das gewünschte Gehäuse immer zuerst aufgebraucht und ein Tausch entsteht
   * nur für die tatsächlich fehlende Restmenge.
   * @param {string} handle
   * @param {number} neededQty
   * @param {number} preferredId
   * @param {{byHandle: Map<string, PcwVariantInfo[]>, bySignature: Map<string, PcwVariantInfo[]>}} maps
   */
  function allocateGatewayVariants(handle, neededQty, preferredId, maps) {
    const all = (maps.byHandle.get(handle) || []).slice();
    /** @type {{info: PcwVariantInfo, qty: number}[]} */
    const allocations = [];
    if (!all.length) return { allocations, shortfall: neededQty };

    const preferred = all.find(v => v.id === preferredId);
    const sameProduct = preferred
      ? all.filter(v => v.variantSig === preferred.variantSig)
      : all;
    if (!sameProduct.length) return { allocations, shortfall: neededQty };

    const wantedGehaeuse = preferred ? preferred.gehaeuse : '';

    // Geschwister: gleiche Ware, anderes Gehäuse. Nur wenn beide Seiten ein Gehäuse tragen —
    // ohne gepflegtes Gehäuse gibt es keine belastbare Geschwister-Beziehung, dann bleibt es
    // beim bisherigen Verhalten innerhalb des Produkts.
    /** @type {PcwVariantInfo[]} */
    let siblings = [];
    if (preferred && wantedGehaeuse) {
      siblings = (maps.bySignature.get(preferred.variantSig) || []).filter(v =>
        v.gehaeuse && v.gehaeuse !== wantedGehaeuse && !sameProduct.some(own => own.id === v.id)
      );
    }

    // Unbegrenzte Bestände sind Infinity; Infinity - Infinity wäre NaN und damit ein
    // undefinierter Vergleich. Deshalb vorher auf eine endliche Rangzahl abbilden.
    const rank = (/** @type {PcwVariantInfo} */ v) => {
      const menge = variantVerfuegbar(v);
      return Number.isFinite(menge) ? menge : Number.MAX_SAFE_INTEGER;
    };
    const byAvailability = (/** @type {PcwVariantInfo} */ a, /** @type {PcwVariantInfo} */ b) =>
      rank(b) - rank(a);

    const ownFirst = sameProduct.slice().sort((a, b) => {
      if (a.id === preferredId) return -1;
      if (b.id === preferredId) return 1;
      return byAvailability(a, b);
    });

    const variants = ownFirst.concat(siblings.slice().sort(byAvailability));

    let remaining = neededQty;
    for (const v of variants) {
      if (remaining <= 0) break;
      const take = Math.min(remaining, variantVerfuegbar(v));
      if (take > 0) {
        allocations.push({ info: v, qty: take });
        remaining -= take;
      }
    }
    return { allocations, shortfall: Math.max(0, remaining), wantedGehaeuse };
  }

  async function postToCart(items) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json().catch(() => null);
    return { status: res.status, ok: res.ok, data };
  }

  async function addToCart() {
    const positionen = berechne();

    if (hasInquirePosition(positionen)) {
      setInquireMode(true);
      elInquire?.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'center' });
      return;
    }

    const cartCandidates = positionen
      .filter(p => p.variantId && !p.cartExcluded);

    if (!cartCandidates.length) {
      alert('Aktuell sind keine kaufbaren Produkte ausgewählt.');
      return;
    }

    elBtnKaufen.disabled = true;
    elBtnKaufen.textContent = 'Verfügbarkeit prüfen…';

    try {
      const availability = buildAvailability();
      const { byVariant } = availability;

      const adjustedItems = [];
      const reductions = [];
      const sellouts = [];
      /** @type {string[]} */
      const switches = [];
      // Gehäuse-Tausche werden getrennt gesammelt: sie sind der einzige Fall, in dem der
      // Kunde eine andere Ware bekommt als angezeigt, und brauchen daher eigene Klartext-
      // Zeilen mit Stückzahlen statt der generischen Varianten-Meldung.
      /** @type {string[]} */
      const housingSwaps = [];

      cartCandidates.forEach(p => {
        if (p.variantId == null) return;
        let variantId = p.variantId;
        let qty = p.menge;

        const prod = findProductByVariantId(p.variantId);
        const isGateway = prod?.rolle === 'gateway';

        if (isGateway && prod?.productHandle) {
          const { allocations, shortfall, wantedGehaeuse } =
            allocateGatewayVariants(prod.productHandle, qty, p.variantId, availability);

          if (!allocations.length) {
            sellouts.push(`• ${p.label}: aktuell ausverkauft (alle Varianten)`);
            return;
          }

          allocations.forEach(a => adjustedItems.push({ id: a.info.id, quantity: a.qty }));

          const swapped = wantedGehaeuse
            ? allocations.filter(a => a.info.gehaeuse && a.info.gehaeuse !== wantedGehaeuse)
            : [];

          if (swapped.length) {
            const ownQty = allocations
              .filter(a => !swapped.includes(a))
              .reduce((sum, a) => sum + a.qty, 0);
            const swapParts = swapped.map(a => `${a.qty}× ${a.info.gehaeuse}`).join(', ');
            housingSwaps.push(
              ownQty > 0
                ? fmtLabel(LBL.linePartial, {
                  label: p.label, available: ownQty, wanted: wantedGehaeuse, fallback: swapParts,
                })
                : fmtLabel(LBL.lineNone, {
                  label: p.label, wanted: wantedGehaeuse, fallback: swapParts,
                })
            );
          } else {
            const first = allocations[0];
            if (allocations.length > 1) {
              const parts = allocations.map(a => `${a.qty}× „${a.info.title}"`).join(', ');
              switches.push(`• ${p.label}: auf mehrere Varianten verteilt (${parts})`);
            } else if (first && first.info.id !== p.variantId) {
              const fromTitle = byVariant.get(p.variantId)?.title || 'gewählte Variante';
              switches.push(`• ${p.label}: „${fromTitle}" ausverkauft → „${first.info.title}" wird verwendet`);
            }
          }

          if (shortfall > 0) {
            reductions.push(`• ${p.label}: nur ${qty - shortfall} von ${qty} verfügbar`);
          }
          return;
        }

        const inv = byVariant.get(p.variantId);
        if (inv && inv.availableBool === false) {
          sellouts.push(`• ${p.label}: aktuell ausverkauft`);
          qty = 0;
        } else if (inv && inv.policy === 'deny' && Number.isFinite(inv.available) && inv.available < qty) {
          if (inv.available > 0) {
            reductions.push(`• ${p.label}: nur ${inv.available} von ${qty} verfügbar`);
            qty = inv.available;
          } else {
            sellouts.push(`• ${p.label}: aktuell ausverkauft`);
            qty = 0;
          }
        }
        if (qty > 0) {
          const item = { id: variantId, quantity: qty };
          if (p.sellingPlanId) item.selling_plan = p.sellingPlanId;
          adjustedItems.push(item);
        }
      });

      if (adjustedItems.length === 0) {
        elBtnKaufen.disabled = false;
        elBtnKaufen.textContent = cfg.labels.btnKaufen;
        alert(
          'Aktuell sind keine der gewählten Produkte verfügbar:\n\n' +
          sellouts.join('\n') +
          '\n\nBitte kontaktieren Sie uns für eine individuelle Lösung.'
        );
        return;
      }

      if (switches.length) {
        console.log('[PCW] Gateway-Varianten-Fallback:\n' + switches.join('\n'));
      }

      // Der Gehäuse-Tausch bekommt eine eigene, zuerst gestellte Rückfrage: der Kunde
      // bekommt hier eine andere Ware als angezeigt, das darf nicht in einer Sammelmeldung
      // untergehen. Bricht er ab, wandert nichts in den Warenkorb.
      if (housingSwaps.length) {
        const ok = await confirmDialog({
          title: LBL.title,
          text: LBL.intro,
          bullets: housingSwaps,
          note: LBL.note,
          confirmLabel: LBL.confirm,
          cancelLabel: LBL.cancel,
        });
        if (!ok) {
          elBtnKaufen.disabled = false;
          elBtnKaufen.textContent = cfg.labels.btnKaufen;
          return;
        }
      }

      if (switches.length > 0 || reductions.length > 0 || sellouts.length > 0) {
        /** @type {string[]} */
        const bullets = [];
        switches.forEach(entry => bullets.push(entry.replace(/^•\s*/, '')));
        reductions.forEach(entry => bullets.push(entry.replace(/^•\s*/, '')));
        sellouts.forEach(entry => bullets.push(entry.replace(/^•\s*/, '')));

        const proceed = await confirmDialog({
          title: LBL.checkTitle,
          bullets,
          note: LBL.checkNote,
          confirmLabel: LBL.confirm,
          cancelLabel: LBL.cancel,
        });
        if (!proceed) {
          elBtnKaufen.disabled = false;
          elBtnKaufen.textContent = cfg.labels.btnKaufen;
          return;
        }
      }

      elBtnKaufen.textContent = 'Wird hinzugefügt…';
      const result = await postToCart(adjustedItems);
      console.log('[PCW] Cart-Add Antwort:', result.status, result.data);

      if (result.ok) {
        window.location.href = '/cart';
        return;
      }

      if (result.status === 422) {
        const shopifyMsg = result.data?.description || result.data?.message || 'Unbekannter Fehler';
        const proceed = confirm(
          'Hinweis von Shopify:\n\n' + shopifyMsg +
          '\n\nDie aktuell verfügbaren Mengen wurden bereits in den Warenkorb gelegt.' +
          '\n\nZum Warenkorb wechseln?'
        );
        if (proceed) {
          window.location.href = '/cart';
          return;
        }
        elBtnKaufen.disabled = false;
        elBtnKaufen.textContent = cfg.labels.btnKaufen;
        return;
      }

      throw new Error(`HTTP ${result.status}: ${result.data?.description || result.data?.message || 'Unbekannter Fehler'}`);
    } catch (err) {
      console.error('[PCW] Cart error:', err);
      elBtnKaufen.disabled = false;
      elBtnKaufen.textContent = cfg.labels.btnKaufen;
      alert('Fehler beim Hinzufügen zum Warenkorb: ' + (err.message || 'Unbekannter Fehler') + '\n\nBitte versuchen Sie es erneut oder kontaktieren Sie uns.');
    }
  }

  // --- Email Notification über echten Form-Submit + Shopify-Redirect ---
  // Shopify's {% form 'contact' %} mit Captcha leitet IMMER auf die /challenge-Page
  // weiter. Daher nutzen wir echten HTML-Submit:
  //   1. User klickt Submit → Form-Submit → Page-Navigation
  //   2. Shopify zeigt /challenge-Seite mit Captcha
  //   3. User löst Captcha → Shopify sendet Email + redirected zurück (?contact_posted=true)
  //   4. Wizard-Init erkennt Param + sessionStorage-State → springt zu Schritt 3
  let captchaInitialized = false;
  let notificationSentForCurrentValues = false;

  function initCaptchaWidget() {
    if (captchaInitialized) return;
    const form = root.querySelector('.pcw__notify-form');
    if (!form) {
      pcwDebug('Notify-Form nicht gefunden');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]');
    if (!submitBtn) {
      pcwDebug('Submit-Button nicht gefunden');
      return;
    }

    // iOS Safari Fix: Alle Vorbereitungen ins click-Event verlagert, NICHT ins submit-Event.
    submitBtn.addEventListener('click', (e) => {
      pcwDebug('Submit-Button geklickt');

      const { text: summaryText } = buildSummary();
      const { name, email } = fillNotifyBodyAndPhone(form, summaryText);

      if (!email) {
        e.preventDefault();
        const elNotifyEmail = $(`#${sectionId}-notify-email`);
        elNotifyEmail?.focus();
        showEmailStatus('Bitte gültige E-Mail-Adresse eingeben.', true);
        pcwDebug('Submit blockiert — keine Email');
        return;
      }
      if (!name) {
        e.preventDefault();
        const elNotifyName = $(`#${sectionId}-notify-name`);
        elNotifyName?.focus();
        showEmailStatus('Bitte Namen eingeben.', true);
        pcwDebug('Submit blockiert — kein Name');
        return;
      }

      state.email = email;
      const nameParts = name.split(/\s+/);
      if (nameParts.length >= 2) {
        state.vorname = nameParts[0];
        state.nachname = nameParts.slice(1).join(' ');
      } else {
        state.vorname = name;
      }

      // State + Flag persistieren VOR Form-Submission
      try {
        state.currentStep = 2;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        sessionStorage.setItem('pcw-pending-submit', '1');
      } catch (_) { /* ignore */ }

      showEmailStatus('Berechnung wird übermittelt…');
      pcwDebug('Form-Submit folgt — Shopify übernimmt Captcha');
      // KEIN preventDefault — native Form-Submission läuft direkt aus dieser User-Geste.
    });

    // Defensive Validation falls Submit programmatisch ausgelöst wird (z.B. Enter-Taste)
    form.addEventListener('submit', (e) => {
      const emailInput = form.querySelector('[name="contact[email]"]');
      if (!emailInput || !emailInput.value || !emailInput.value.includes('@')) {
        e.preventDefault();
        showEmailStatus('Bitte gültige E-Mail-Adresse eingeben.', true);
        pcwDebug('Submit-Event ohne Email-Adresse abgebrochen');
        return;
      }
      pcwDebug('Submit-Event durchgelassen');
    });

    captchaInitialized = true;
  }

  // --- Offer data ---
  // One single source for every rendering of the calculation: contact field, lead
  // mail and PDF. There used to be two text builders with diverging formats — a
  // third consumer must not carry that on.
  function buildOffer() {
    const positionen = berechne();
    const counts = (/** @type {any} */ p, /** @type {string} */ period) =>
      p.periode === period && hatPreis(p);
    const sumOf = (/** @type {string} */ period) =>
      positionen.filter(p => counts(p, period)).reduce((s, p) => s + p.betrag, 0);
    const oneOff = sumOf('einmalig');
    const monthly = sumOf('monatlich');
    const yearly = sumOf('jaehrlich');

    return {
      customer: {
        firstName: state.vorname,
        lastName: state.nachname,
        name: `${state.vorname} ${state.nachname}`.trim(),
        email: state.email,
      },
      facts: [
        { label: 'Anzahl Immobilien', value: state.immoCount },
        { label: 'Wohneinheiten gesamt', value: getGesamtWE() },
        { label: 'Benötigte Datensammler', value: getGwMenge() },
      ],
      properties: state.properties.map((p, i) => {
        const t = effectiveType(p);
        const dev = t ? deviceFor(t, housingFor(p)) : null;
        return {
          index: i + 1,
          units: Number(p.we) || 0,
          socket: p.s,
          wifi: p.w,
          housing: dev ? dev.gehaeuse : housingFor(p),
          deviceType: t,
          // Ready-made label including the quantity: consumers must not have to
          // reproduce the editor-configurable device names a second time.
          deviceLabel: dev
            ? `${dsCountFor(p.we)}× ${deviceLabel(dev)}`
            : (isAnswered(p) ? '—' : (ASSIST.pendingShort || 'noch offen')),
        };
      }),
      items: positionen.map(p => ({
        label: p.label,
        amount: hatPreis(p) ? p.betrag : INQUIRE_LABEL,
        period: p.periode,
      })),
      totals: { oneOff, monthly, yearly, firstYear: oneOff + monthly * 12 + yearly },
      // Unpriced rows cannot be summed. Without this marker an on-request-only
      // calculation would read as "0,00 €" and a mixed one as too cheap — the
      // totals have to carry the caveat.
      hasOnRequest: positionen.some(p => !hatPreis(p)),
      onRequestLabel: INQUIRE_LABEL,
      createdAt: new Date(),
    };
  }

  // Amount of a line item: format numbers, pass the "on request" wording through.
  function offerAmount(/** @type {number | string} */ amount) {
    return typeof amount === 'number' ? formatPreis(amount) : String(amount);
  }

  /**
   * Text version of the offer — the same sections as the PDF, so customer and team
   * see the same figures. Customer details are optional: in the contact form, name
   * and email already have their own fields; in the lead mail they do not.
   *
   * @param {ReturnType<typeof buildOffer>} offer
   * @param {{withCustomer?: boolean, withTotals?: boolean}} [opts]
   */
  function renderOfferText(offer, opts = {}) {
    const lines = ['Anfrage aus dem Preisrechner:', ''];

    if (opts.withCustomer) {
      lines.push(`Kunde: ${offer.customer.name}`, `E-Mail: ${offer.customer.email}`, '');
    }

    lines.push('Ihre Angaben:');
    offer.facts.forEach(({ label, value }) => lines.push(`- ${label}: ${value}`));
    offer.properties.forEach((p) => {
      lines.push(`- Immobilie ${p.index}: ${p.units} WE · ${p.deviceLabel}`);
    });
    lines.push('');

    lines.push('Kalkulierte Positionen:');
    offer.items.forEach((item) => {
      lines.push(`- ${item.label}: ${offerAmount(item.amount)} (${item.period || '–'})`);
    });

    if (opts.withTotals) {
      const t = offer.totals;
      lines.push(
        '',
        'Summen:',
        `- Einmalige Kosten: ${formatPreis(t.oneOff)}`,
        `- Monatliche Kosten: ${formatPreis(t.monthly)}`,
        `- Jährliche Zusatzkosten: ${formatPreis(t.yearly)}`,
        `- Gesamt Jahr 1: ${formatPreis(t.firstYear)}`
      );
      if (offer.hasOnRequest) {
        lines.push(`  (zzgl. Positionen „${offer.onRequestLabel}“ — nicht in den Summen enthalten)`);
      }
    }

    lines.push('', 'Bitte erstellen Sie uns ein passendes Angebot.');
    return lines.join('\n');
  }

  function buildSummary() {
    const offer = buildOffer();
    return {
      positionen: offer.items,
      einmalig: offer.totals.oneOff,
      monatlich: offer.totals.monthly,
      jaehrlich: offer.totals.yearly,
      text: renderOfferText(offer, { withCustomer: true, withTotals: true }),
    };
  }

  function prefillNotifyForm() {
    const elNotifyName = $(`#${sectionId}-notify-name`);
    const elNotifyEmail = $(`#${sectionId}-notify-email`);
    const vollName = `${state.vorname} ${state.nachname}`.trim();
    if (elNotifyName && !elNotifyName.value) elNotifyName.value = vollName;
    if (elNotifyEmail && !elNotifyEmail.value) elNotifyEmail.value = state.email || '';

    if (elNotifyName && !elNotifyName.dataset.pcwBound) {
      elNotifyName.dataset.pcwBound = '1';
      elNotifyName.addEventListener('input', () => {
        notificationSentForCurrentValues = false;
      });
    }
    if (elNotifyEmail && !elNotifyEmail.dataset.pcwBound) {
      elNotifyEmail.dataset.pcwBound = '1';
      elNotifyEmail.addEventListener('input', () => {
        notificationSentForCurrentValues = false;
      });
    }
  }

  function fillNotifyBodyAndPhone(form, summary) {
    const elNotifyPhone = $(`#${sectionId}-notify-phone`);
    const elNotifyBody = $(`#${sectionId}-notify-body`);
    if (elNotifyPhone) elNotifyPhone.value = '';
    if (elNotifyBody) elNotifyBody.value = summary;

    const elNotifyName = $(`#${sectionId}-notify-name`);
    const elNotifyEmail = $(`#${sectionId}-notify-email`);
    const nameVal = elNotifyName ? elNotifyName.value.trim() : '';
    const emailVal = elNotifyEmail ? elNotifyEmail.value.trim() : '';
    return { name: nameVal, email: (emailVal && emailVal.includes('@')) ? emailVal : '' };
  }

  // Beim Init prüfen ob wir von Shopify mit Erfolgs-Indikator zurückkommen
  function checkContactPosted() {
    try {
      const params = new URLSearchParams(window.location.search);
      const hasParam = params.get('contact_posted') === 'true';
      const hasHash = window.location.hash === '#contact_form';
      const isReturnFromShopify = hasParam || hasHash;

      if (isReturnFromShopify) {
        sessionStorage.removeItem('pcw-pending-submit');
        notificationSentForCurrentValues = true;
        pcwDebug('Rückkehr von Contact-Form-Submit erkannt');
        return true;
      } else {
        sessionStorage.removeItem('pcw-pending-submit');
      }
    } catch (_) { /* ignore */ }
    return false;
  }

  function showEmailStatus(msg, isError) {
    if (!elEmailStatus) return;
    elEmailStatus.textContent = msg;
    elEmailStatus.style.color = isError ? '#d32f2f' : 'var(--text-muted, #555577)';
    elEmailStatus.classList.add('is-visible');
    if (!isError) {
      setTimeout(() => elEmailStatus.classList.remove('is-visible'), 5000);
    }
  }

  // --- Request offer ---
  // Stores the calculation for the contact page (contact-tabs.js reads the same key).
  // Kept separate from the redirect because the inquire card in step 3 is a real
  // link: there we only store and let the browser navigate.
  function storeKalkulation() {
    const offer = buildOffer();

    // The payload keys stay as they are — they are the wire format that
    // contact-tabs.js and older, still-open tabs read.
    const data = {
      vorname: offer.customer.firstName,
      nachname: offer.customer.lastName,
      email: offer.customer.email,
      immoCount: state.immoCount,
      properties: offer.properties.map(p => ({
        steckdose: p.socket,
        wlan: p.wifi,
        gehaeuse: p.housing,
        we: p.units,
        geraet: p.deviceType,
        geraetLabel: p.deviceLabel,
      })),
      gesamtWE: getGesamtWE(),
      gatewayMenge: getGwMenge(),
      positionen: offer.items.map(item => ({
        label: item.label,
        betrag: item.amount,
        periode: item.period,
      })),
      // Ready-made text: contact-tabs.js must not restate the format. The single
      // fields stay anyway — they fill name/email and serve as the fallback.
      message: renderOfferText(offer, { withTotals: true }),
    };
    try {
      sessionStorage.setItem('pcw-kalkulation', JSON.stringify(data));
    } catch (_) { /* ignore */ }
  }

  function angebotAnfordern() {
    storeKalkulation();
    window.location.href = cfg.contactPageUrl;
  }

  // --- Offer PDF ---
  // Layout and PDF generation live in assets/pcw-pdf.js; only the wiring stays
  // here, so the wizard keeps the offer semantics and the module stays testable
  // on its own.
  //
  // Remember the original label ONCE and keep the error timer: otherwise a second
  // click inside the error window would read the error text as the "original",
  // and the still-pending timer would restore it after a successful download.
  let pdfLabelTimer = 0;
  const pdfLabel = elBtnPdf ? elBtnPdf.textContent : '';

  async function downloadOffer() {
    if (!elBtnPdf) return;
    clearTimeout(pdfLabelTimer);
    elBtnPdf.disabled = true;
    elBtnPdf.textContent = PDF.busyLabel || 'PDF wird erstellt …';
    try {
      const offer = buildOffer();
      // Loaded on click only — otherwise the writer sits unused on the wire.
      const { renderOfferSheet, offerFilename, downloadBlob } = await import('@theme/pcw-pdf');
      const doc = await renderOfferSheet(
        // seed = the text version: same calculation, same quote number.
        Object.assign({}, offer, { seed: renderOfferText(offer) }),
        {
          docTitle: PDF.docTitle,
          company: PDF.company,
          logoUrl: PDF.logoUrl,
          validityDays: PDF.validityDays,
          footerNote: PDF.footerNote,
          formatMoney: formatPreis,
        }
      );
      downloadBlob(doc.toBlob(), offerFilename(offer.createdAt, PDF.filenamePrefix));
      elBtnPdf.textContent = pdfLabel;
    } catch (err) {
      console.error('[PCW] PDF generation failed', err);
      elBtnPdf.textContent = PDF.errorLabel || 'PDF fehlgeschlagen — bitte erneut versuchen';
      pdfLabelTimer = setTimeout(() => { elBtnPdf.textContent = pdfLabel; }, 6000);
    } finally {
      elBtnPdf.disabled = false;
    }
  }

  // --- Neu Berechnen ---
  // Start over means start over: the step-1 answers, the package and service selection
  // and the contact fields of the notification gate all go back to their defaults.
  // Clearing only the packages left the wizard showing the previous inputs, which reads
  // as a broken button.
  //
  // Exception: the OMS answer survives. It describes the meters already installed at the
  // customer's site, not this calculation — re-asking it on every recalculation would be
  // pure friction (and it gates step 1, so they could not get past it either).
  function neuBerechnen() {
    const omsStatus = state.omsStatus;
    state = freshState();
    state.omsStatus = omsStatus;

    // Everything below is DOM that is not rebuilt from state on its own.
    $$('.pcw__option input[type="checkbox"]', root).forEach(cb => { cb.checked = false; });
    selectTier(null);            // clears radio + card highlight of the portal packages
    applyOmsSelectionClasses();  // re-marks the kept answer
    syncToggleUI();
    ['notify-name', 'notify-email', 'notify-phone', 'notify-body'].forEach(suffix => {
      const el = $(`#${sectionId}-${suffix}`);
      if (el) el.value = '';
    });
    if (elEmailStatus) {
      elEmailStatus.textContent = '';
      elEmailStatus.classList.remove('is-visible');
    }
    hideStep1Alert();
    if (elImmo) {
      elImmo.value = String(state.immoCount);
      if (elImmoVal) elImmoVal.textContent = String(state.immoCount);
      updateSliderFill(elImmo, 1, MAX_OBJEKTE);
    }

    notificationSentForCurrentValues = false;
    updateStep1();
    saveState();
    goToStep(1);
  }

  // --- Restore State ---
  function restoreFormState() {
    // properties/immoCount kommen aus dem geladenen State; Defaults absichern.
    if (!Array.isArray(state.properties) || !state.properties.length) {
      state.properties = Array.from({ length: START_OBJEKTE }, () => makeProperty());
    }
    // Restored properties predate the visit gate (or were configured earlier in this
    // session): they count as seen. Must run before growProperties, whose fresh
    // properties are deliberately unseen.
    state.properties.forEach(p => { if (p && typeof p.seen !== 'boolean') p.seen = true; });
    state.immoCount = clampInt(state.immoCount || state.properties.length, 1, MAX_OBJEKTE);
    growProperties(state.immoCount);
    // WE-Werte aufs aktuelle Raster ziehen: der Admin kann die Schrittweite zwischen zwei
    // Sitzungen geändert haben, sodass ein gespeicherter Wert nicht mehr auf dem Raster liegt.
    state.properties.forEach(p => { p.we = snapWe(p.we); });
    if (typeof state.openIndex !== 'number' || state.openIndex >= state.immoCount) {
      state.openIndex = state.immoCount - 1;
    }
    if (elImmo) {
      elImmo.value = String(state.immoCount);
      if (elImmoVal) elImmoVal.textContent = String(state.immoCount);
      updateSliderFill(elImmo, 1, MAX_OBJEKTE);
    }
    updateStep1();

    if (state.selectedPortalVariantId) {
      selectTier(state.selectedPortalVariantId);
    }

    state.selectedDiensteIds.forEach(id => {
      const cb = $(`.pcw__option input[value="${id}"]`, root);
      if (cb) cb.checked = true;
    });

    applyOmsSelectionClasses();
    syncToggleUI();
  }

  // --- Input Sync (Immobilien-Slider) ---
  function setupInputSync() {
    if (elImmo) {
      updateSliderFill(elImmo, 1, MAX_OBJEKTE);

      // Während eines Pointer-Drags am Immobilien-Slider darf NICHT bei jedem input
      // setImmoCount → renderAssistant laufen: das Neu-Aufbauen aller Immobilien-Kacheln
      // (die sich dann „setzen") unterbricht den Drag. Stattdessen pro Frame nur billig
      // Füllung/Wert/Zustand/Summenzahlen ziehen; die Kacheln erst beim Loslassen neu bauen.
      let immoDragging = false;
      let immoRaf = 0;
      const immoFrame = () => {
        const n = clampInt(elImmo.value, 1, MAX_OBJEKTE);
        updateSliderFill(elImmo, 1, MAX_OBJEKTE);
        if (elImmoVal) elImmoVal.textContent = String(n);
        state.immoCount = n;
        growProperties(n);        // billig (nur Array), KEIN DOM-Rebuild
        updateSummaryLive();
        immoRaf = requestAnimationFrame(immoFrame);
      };

      elImmo.addEventListener('input', () => {
        if (immoDragging) return; // Drag-Schleife übernimmt; teures Rebuild vermeiden
        setImmoCount(elImmo.value);
      });

      elImmo.addEventListener('pointerdown', () => {
        if (immoDragging) return;
        immoDragging = true;
        immoRaf = requestAnimationFrame(immoFrame);
        const stop = () => {
          immoDragging = false;
          if (immoRaf) { cancelAnimationFrame(immoRaf); immoRaf = 0; }
          setImmoCount(elImmo.value); // finaler Commit: Kacheln + openIndex + speichern
          window.removeEventListener('pointerup', stop);
          window.removeEventListener('pointercancel', stop);
        };
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
      });
    }
  }

  // --- Event Binding ---
  function bindEvents() {
    elBtnNext?.addEventListener('click', () => {
      if (!validateStep(state.currentStep)) return;
      // Mit aktivem Kontakt-Gate: in Schritt 2 den Captcha-Submit-Flow auslösen.
      if (state.currentStep === 2 && CONTACT_GATE) {
        const submitBtn = root.querySelector('.pcw__notify-submit-btn');
        if (submitBtn) {
          submitBtn.click();
          return;
        }
      }
      goToStep(state.currentStep + 1);
    });

    elBtnPrev?.addEventListener('click', () => {
      goToStep(state.currentStep - 1);
    });

    elStepBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const stepNum = parseInt(btn.dataset.step, 10);
        if (stepNum <= state.maxVisitedStep) {
          goToStep(stepNum);
        }
      });
    });

    elBtnKaufen?.addEventListener('click', addToCart);
    elBtnAngebot?.addEventListener('click', angebotAnfordern);
    elBtnPdf?.addEventListener('click', downloadOffer);
    elBtnNeu?.addEventListener('click', neuBerechnen);

    elInquireBtn?.addEventListener('click', () => {
      storeKalkulation();
    });

    // Kontakt-Button bei WE-Obergrenze: Eingaben für die Kontaktseite ablegen.
    elWeLimitBtn?.addEventListener('click', () => {
      try {
        sessionStorage.setItem('pcw-kalkulation', JSON.stringify({
          ueberLimit: true,
          maxWE: MAX_WE,
          immoCount: state.immoCount,
          gesamtWE: getGesamtWE(),
        }));
      } catch (_) { /* ignore */ }
    });
  }

  // --- Init ---
  function init() {
    const hadState = loadState();
    const contactPosted = checkContactPosted();
    pcwDebug(`Init: hadState=${hadState}, contactPosted=${contactPosted}, url=${window.location.search}`);

    setupAssistant();
    setupOms();
    setupTiers();
    setupFeatureToggles();
    setupDienste();
    setupResultToggle();
    setupInputSync();
    bindEvents();

    if (hadState) {
      restoreFormState();
      // Wenn von Captcha-Erfolg zurück: direkt zu Schritt 3 (Berechnung anzeigen)
      if (contactPosted) {
        state.maxVisitedStep = Math.max(state.maxVisitedStep, TOTAL_STEPS);
        pcwDebug('Sprung zu Schritt 3 (Captcha erfolgreich)');
        goToStep(3);
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('contact_posted');
          window.history.replaceState({}, '', url.toString());
        } catch (_) { /* ignore */ }
      } else {
        const targetStep = Math.min(state.currentStep, state.maxVisitedStep);
        pcwDebug(`State wiederhergestellt → Schritt ${targetStep}`);
        goToStep(targetStep);
      }
    } else {
      pcwDebug('Kein State — Start bei Schritt 1');
      goToStep(1);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
